import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { StatusBadge } from '../components/StatusBadge';
import { PremiumBadge } from '../components/PremiumBadge';
import { AdminSupportInbox } from '../components/AdminSupportInbox';
import { AdminAuditLog } from '../components/AdminAuditLog';
import AdminConversations from '../components/AdminConversations';
import AdminNotesDialog from '../components/AdminNotesDialog';
import InsuranceReviewPanel from '../components/admin/InsuranceReviewPanel';
import AdminDirectoryApprovals from '../components/admin/AdminDirectoryApprovals';
import { NotificationPrompt, NotificationToggle } from '../components/NotificationPrompt';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription } from '../components/ui/alert';
import { getAllDeliveries, getOffers, getAdminStats, getRevenueStats, getAllUsers, editUser, deleteUser, deleteDelivery, bulkDeleteDeliveries, bulkDeleteUsers, toggleBanUser, getAdminDriverDetails, getDriversByCounty, getDriverLicenceFile, reviewDriverCompliance, cancelAuthorizedPayment, refundDelivery, adminForceCapture, resendReceipt, nudgeCustomer, getOwedCompensations, retryCompensationTransfer, toggleDriverPremium, supportAdminThreads, resolveAssetUrl } from '../services/api';
import CancelWithCompensationDialog from '../components/admin/CancelWithCompensationDialog';
import AdminSideNav, { ADMIN_NAV_LOOKUP } from '../components/admin/AdminSideNav';
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import DriverPhotoWall from '../components/DriverPhotoWall';
import DriverBioWall from '../components/DriverBioWall';
import { toast } from 'sonner';
import { Menu, Package, Users, TrendingUp, Clock, Check, Eye, Euro, CheckCircle, CreditCard, Shield, Pencil, Trash2, Ban, ShieldCheck, ShieldAlert, UserCog, AlertTriangle, Search, Star, Briefcase, User, MapPin, Loader2, Mail, KeyRound, Copy, Camera, Activity, Hourglass, MessageCircle, X, Globe, HandCoins, Truck, StickyNote } from 'lucide-react';
import EarningsPanel from '../components/admin/EarningsPanel';
import HomepageCMSPanel from '../components/admin/HomepageCMSPanel';

const DIRECT_METHOD_LABELS = {
  cash: 'Cash',
  revolut: 'Revolut',
  bank_transfer: 'Bank Transfer',
  zippay: 'ZipPay',
  other: 'Other',
};

export default function AdminDashboard() {
  const [deliveries, setDeliveries] = useState([]);
  const [stats, setStats] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [revenuePeriod, setRevenuePeriod] = useState('all_time');
  const [loading, setLoading] = useState(true);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [offers, setOffers] = useState([]);
  const [offersLoading, setOffersLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [mainTab, setMainTab] = useState('deliveries');
  const [supportUnread, setSupportUnread] = useState(0);
  const [searchParams] = useSearchParams();

  // Open the Support tab automatically when arriving from a notification deep link.
  useEffect(() => {
    const t = searchParams.get('tab');
    if (t === 'support' || t === 'users' || t === 'deliveries' || t === 'activity') {
      setMainTab(t);
    }
  }, [searchParams]);

  // Poll the support inbox for total unread so the Support tab can show a badge.
  useEffect(() => {
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const res = await supportAdminThreads();
        const total = (res.data?.threads || []).reduce((s, t) => s + (t.unread || 0), 0);
        if (!cancelled) setSupportUnread(total);
      } catch { /* silent */ }
    };
    fetchUnread();
    const id = setInterval(fetchUnread, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [mainTab]);

  // User management state
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', phone: '', role: '', password: '' });
  const [saving, setSaving] = useState(false);
  const [userFilter, setUserFilter] = useState('all');
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [usersPage, setUsersPage] = useState(1);
  const [deleteDeliveryId, setDeleteDeliveryId] = useState(null);
  const [selectedDeliveryIds, setSelectedDeliveryIds] = useState([]);
  const [bulkDeleteDeliveriesOpen, setBulkDeleteDeliveriesOpen] = useState(false);

  // Driver detail state
  const [driverDetailOpen, setDriverDetailOpen] = useState(false);
  const [driverDetailData, setDriverDetailData] = useState(null);
  const [driverDetailLoading, setDriverDetailLoading] = useState(false);
  const [driverDetailUser, setDriverDetailUser] = useState(null);
  const [driversByCounty, setDriversByCounty] = useState([]);
  const [countyFilter, setCountyFilter] = useState('');
  // Users tab inner view — 'list' (default existing table) or 'photo-wall'
  // (new admin gallery for rapid driver-photo moderation).
  const [usersInnerView, setUsersInnerView] = useState('list');
  // Compliance review modal
  const [complianceUser, setComplianceUser] = useState(null);
  const [complianceLicence, setComplianceLicence] = useState(null);
  const [complianceLicenceBack, setComplianceLicenceBack] = useState(null);
  const [complianceLoading, setComplianceLoading] = useState(false);
  const [complianceNote, setComplianceNote] = useState('');
  // Quick-view licence dialog (any driver, any time — independent of compliance review flow)
  const [licenceViewUser, setLicenceViewUser] = useState(null);
  const [licenceViewSrc, setLicenceViewSrc] = useState(null);
  const [licenceViewBackSrc, setLicenceViewBackSrc] = useState(null);
  const [licenceViewLoading, setLicenceViewLoading] = useState(false);

  const openLicenceViewer = async (u) => {
    setLicenceViewUser(u);
    setLicenceViewSrc(null);
    setLicenceViewBackSrc(null);
    setLicenceViewLoading(true);
    try {
      const res = await getDriverLicenceFile(u.id);
      const src = res.data?.licence_file || null;
      const back = res.data?.licence_file_back || null;
      setLicenceViewSrc(src);
      setLicenceViewBackSrc(back);
      if (!src && !back) toast.error('No licence on file for this driver.');
    } catch {
      toast.error('Failed to load licence');
    } finally {
      setLicenceViewLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const fetchRevenue = async () => {
      try {
        const res = await getRevenueStats(revenuePeriod);
        setRevenue(res.data);
      } catch (error) {
        toast.error('Failed to fetch revenue');
      }
    };
    fetchRevenue();
  }, [revenuePeriod]);


  const fetchData = async () => {
    try {
      const [deliveriesRes, statsRes, revenueRes] = await Promise.all([
        getAllDeliveries(),
        getAdminStats(),
        getRevenueStats(revenuePeriod)
      ]);
      setDeliveries(deliveriesRes.data);
      setStats(statsRes.data);
      setRevenue(revenueRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const [res, cityRes] = await Promise.all([getAllUsers(), getDriversByCounty()]);
      setUsers(res.data);
      setDriversByCounty(cityRes.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setUsersLoading(false);
    }
  };

  // Fetch users when switching to users tab
  useEffect(() => {
    if (mainTab === 'users' && users.length === 0) {
      fetchUsers();
    }
  }, [mainTab]);

  const handleDeleteDelivery = async () => {
    if (!deleteDeliveryId) return;
    setSaving(true);
    try {
      await deleteDelivery(deleteDeliveryId);
      toast.success('Delivery deleted');
      setDeleteDeliveryId(null);
      setSelectedDeliveryIds(prev => prev.filter(id => id !== deleteDeliveryId));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete delivery');
    } finally {
      setSaving(false);
    }
  };

  const handleBulkDeleteDeliveries = async () => {
    if (selectedDeliveryIds.length === 0) return;
    setSaving(true);
    try {
      const res = await bulkDeleteDeliveries(selectedDeliveryIds);
      toast.success(res.data.message);
      setSelectedDeliveryIds([]);
      setBulkDeleteDeliveriesOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete deliveries');
    } finally {
      setSaving(false);
    }
  };

  const toggleSelectDelivery = (id) => {
    setSelectedDeliveryIds(prev => prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id]);
  };

  const toggleSelectAllDeliveries = () => {
    if (deliveries.length > 0 && deliveries.every(d => selectedDeliveryIds.includes(d.id))) {
      setSelectedDeliveryIds([]);
    } else {
      setSelectedDeliveryIds(deliveries.map(d => d.id));
    }
  };

  const viewOffers = async (delivery) => {
    setSelectedDelivery(delivery);
    setDialogOpen(true);
    setOffersLoading(true);
    try {
      const response = await getOffers(delivery.id);
      setOffers(response.data);
    } catch (error) {
      toast.error('Failed to fetch offers');
    } finally {
      setOffersLoading(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      const parsed = JSON.parse(dateString);
      const d = new Date(parsed.date + 'T00:00:00');
      const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      const fmtTime = (t) => { const [h,m] = t.split(':'); const hr = parseInt(h); return `${hr > 12 ? hr-12 : hr || 12}:${m} ${hr >= 12 ? 'PM' : 'AM'}`; };
      if (parsed.type === 'before') return `${dateStr} by ${fmtTime(parsed.before)}`;
      if (parsed.type === 'range') return `${dateStr}, ${fmtTime(parsed.from)} – ${fmtTime(parsed.to)}`;
      return `${dateStr}, Flexible`;
    } catch {
      return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    }
  };

  const [deliverySearch, setDeliverySearch] = useState('');
  const [deliveryPeriod, setDeliveryPeriod] = useState('all_time');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('admin_sidebar_collapsed') === '1');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [adminDeliveryPage, setAdminDeliveryPage] = useState(1);

  const filterDeliveries = (status) => {
    let filtered = status === 'all' ? deliveries : deliveries.filter(d => d.status === status);
    if (deliveryPeriod !== 'all_time') {
      const now = new Date();
      let start;
      if (deliveryPeriod === 'today') { start = new Date(now.getFullYear(), now.getMonth(), now.getDate()); }
      else if (deliveryPeriod === 'this_week') { start = new Date(now); start.setDate(now.getDate() - now.getDay()); start.setHours(0,0,0,0); }
      else if (deliveryPeriod === 'this_month') { start = new Date(now.getFullYear(), now.getMonth(), 1); }
      else if (deliveryPeriod === 'this_year') { start = new Date(now.getFullYear(), 0, 1); }
      if (start) filtered = filtered.filter(d => d.created_at && new Date(d.created_at) >= start);
    }
    if (deliverySearch.trim()) {
      const q = deliverySearch.toLowerCase();
      filtered = filtered.filter(d =>
        d.delivery_ref?.toLowerCase().includes(q) ||
        d.pickup_location?.toLowerCase().includes(q) ||
        d.dropoff_location?.toLowerCase().includes(q) ||
        d.pickup_county?.toLowerCase().includes(q) ||
        d.dropoff_county?.toLowerCase().includes(q) ||
        d.customer_name?.toLowerCase().includes(q) ||
        d.item_description?.toLowerCase().includes(q) ||
        d.id?.includes(q)
      );
    }
    return filtered;
  };

  // ---- User Management Handlers ----

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setEditForm({ name: user.name, email: user.email, phone: user.phone, role: user.role, password: '' });
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      const payload = {};
      if (editForm.name !== selectedUser.name) payload.name = editForm.name;
      if (editForm.email !== selectedUser.email) payload.email = editForm.email;
      if (editForm.phone !== selectedUser.phone) payload.phone = editForm.phone;
      if (editForm.role !== selectedUser.role) payload.role = editForm.role;
      if (editForm.password) payload.password = editForm.password;

      if (Object.keys(payload).length === 0) {
        toast.info('No changes to save');
        setEditDialogOpen(false);
        setSaving(false);
        return;
      }

      await editUser(selectedUser.id, payload);
      toast.success('User updated successfully');
      setEditDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      await deleteUser(selectedUser.id);
      toast.success('User deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      setSelectedUserIds(prev => prev.filter(id => id !== selectedUser.id));
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setSaving(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedUserIds.length === 0) return;
    setSaving(true);
    try {
      const res = await bulkDeleteUsers(selectedUserIds);
      toast.success(res.data.message);
      setSelectedUserIds([]);
      setBulkDeleteDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete users');
    } finally {
      setSaving(false);
    }
  };

  const toggleSelectUser = (userId) => {
    setSelectedUserIds(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
  };

  const toggleSelectAll = () => {
    const nonAdminIds = filteredUsers.filter(u => u.role !== 'admin').map(u => u.id);
    if (nonAdminIds.every(id => selectedUserIds.includes(id))) {
      setSelectedUserIds(prev => prev.filter(id => !nonAdminIds.includes(id)));
    } else {
      setSelectedUserIds(prev => [...new Set([...prev, ...nonAdminIds])]);
    }
  };

  const handleToggleBan = async (user) => {
    try {
      const res = await toggleBanUser(user.id);
      toast.success(res.data.message);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user status');
    }
  };

  // Optimistic toggle so the button feels instant; rollback on failure.
  const [premiumPending, setPremiumPending] = useState({}); // { [userId]: true }
  const [notesUser, setNotesUser] = useState(null); // user whose notes dialog is open
  const handleTogglePremium = async (driver) => {
    const id = driver.id;
    const next = !driver.is_premium;
    setPremiumPending((p) => ({ ...p, [id]: true }));
    setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, is_premium: next } : u)));
    try {
      const res = await toggleDriverPremium(id);
      toast.success(res.data.message || (next ? 'Premium enabled' : 'Premium removed'));
    } catch (error) {
      // Rollback on failure.
      setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, is_premium: !next } : u)));
      toast.error(error.response?.data?.detail || 'Failed to update premium status');
    } finally {
      setPremiumPending((p) => { const c = { ...p }; delete c[id]; return c; });
    }
  };

  const filteredUsers = (
    userFilter === 'all' ? users :
    userFilter === 'pending_review' ? users.filter(u => u.role === 'driver' && (u.compliance_status === 'pending_review' || u.compliance_status === 'restricted')) :
    userFilter === 'business' ? users.filter(u => u.account_type === 'business') :
    userFilter === 'premium' ? users.filter(u => u.role === 'driver' && u.is_premium) :
    users.filter(u => u.role === userFilter)
  )
    .filter(u => {
      // County filter (drivers only)
      if (countyFilter) {
        if (u.role !== 'driver') return false;
        if (countyFilter === 'All Ireland') {
          if (!u.serves_all_ireland) return false;
        } else if (!Array.isArray(u.counties) || !u.counties.includes(countyFilter)) {
          return false;
        }
      }
      if (!userSearch) return true;
      const q = userSearch.toLowerCase();
      return u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q) || u.phone?.toLowerCase().includes(q) || u.company_name?.toLowerCase().includes(q);
    });

  const pendingCount = users.filter(u => u.role === 'driver' && (u.compliance_status === 'pending_review' || u.compliance_status === 'restricted')).length;

  const roleBadge = (role) => {
    const styles = {
      admin: 'bg-red-100 text-red-700',
      driver: 'bg-blue-100 text-blue-700',
      customer: 'bg-gray-100 text-gray-700'
    };
    return (
      <span className={`px-2 py-0.5 text-xs font-medium uppercase tracking-wider ${styles[role] || styles.customer}`}>
        {role}
      </span>
    );
  };

  const openDriverById = (driverId) => {
    const row = users.find(u => u.id === driverId);
    if (row) openDriverDetail(row);
  };


  const openDriverDetail = async (driver) => {
    setDriverDetailUser(driver);
    setDriverDetailOpen(true);
    setDriverDetailLoading(true);
    try {
      const res = await getAdminDriverDetails(driver.id);
      setDriverDetailData(res.data);
    } catch {
      toast.error('Failed to load driver details');
    } finally {
      setDriverDetailLoading(false);
    }
  };

  const openComplianceReview = async (driver) => {
    setComplianceUser(driver);
    setComplianceLicence(null);
    setComplianceLicenceBack(null);
    setComplianceNote('');
    setComplianceLoading(true);
    try {
      const res = await getDriverLicenceFile(driver.id);
      setComplianceLicence(res.data?.licence_file || null);
      setComplianceLicenceBack(res.data?.licence_file_back || null);
    } catch {
      toast.error('Failed to load licence');
    } finally {
      setComplianceLoading(false);
    }
  };

  const handleComplianceDecision = async (decision) => {
    if (!complianceUser) return;
    if (decision === 'reject' && !complianceNote.trim()) {
      toast.error('Please add a short note explaining the rejection.');
      return;
    }
    try {
      const { data } = await reviewDriverCompliance(complianceUser.id, decision, complianceNote.trim() || undefined);
      const mailed = data?.email_sent
        ? ` Email sent to ${complianceUser.email}.`
        : ' ⚠️ We could not email the driver — check the email address.';
      if (decision === 'approve') {
        toast.success(`Driver approved — they can now bid on jobs.${mailed}`);
      } else {
        toast.success(`Driver rejected.${mailed}`);
      }
      setComplianceNote('');
      setComplianceUser(null);
      // Refresh user list so the badge updates
      const refreshed = await getAllUsers();
      setUsers(refreshed.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update compliance');
    }
  };

  const handleRefund = async (delivery) => {
    const totalCharged = (delivery.assigned_price || 0) + (delivery.platform_fee || 0);
    const input = window.prompt(`Refund amount (€). Leave blank for full refund of €${totalCharged.toFixed(2)}.`, '');
    if (input === null) return;
    let amount = null;
    if (input.trim()) {
      amount = parseFloat(input.replace(',', '.'));
      if (Number.isNaN(amount) || amount <= 0) { toast.error('Invalid amount'); return; }
    }
    const reason = window.prompt('Reason (optional):', '') || '';
    try {
      const { data } = await refundDelivery(delivery.id, { amount_eur: amount, reason });
      toast.success(`Refunded €${data.amount_eur.toFixed(2)}${data.full ? ' (full)' : ' (partial)'}.`);
      const refreshed = await getAllDeliveries();
      setDeliveries(refreshed.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Refund failed');
    }
  };

  const handleCancelAuth = async (delivery) => {
    if (!window.confirm('Cancel authorization on this delivery? The customer card will not be charged and the driver will not be paid.')) return;
    try {
      await cancelAuthorizedPayment(delivery.id);
      toast.success('Authorization cancelled.');
      const refreshed = await getAllDeliveries();
      setDeliveries(refreshed.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Cancel failed');
    }
  };

  const handleForceCapture = async (delivery) => {
    if (!window.confirm('Force-capture now (skip the 24h window)? This will charge the customer and pay the driver immediately.')) return;
    try {
      const { data } = await adminForceCapture(delivery.id);
      if (data.ok) toast.success('Captured & transferred.'); else toast.error(data.reason || 'Capture failed');
      const refreshed = await getAllDeliveries();
      setDeliveries(refreshed.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Capture failed');
    }
  };

  const handleResendReceipt = async (delivery) => {
    try {
      const { data } = await resendReceipt(delivery.id);
      toast.success(`Receipt resent to ${data.customer_email} (€${data.total_amount.toFixed(2)}).`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Receipt resend failed');
    }
  };


  const [nudgingId, setNudgingId] = useState(null);
  const [compDelivery, setCompDelivery] = useState(null);
  const [owedComps, setOwedComps] = useState([]);
  const [retryingComp, setRetryingComp] = useState(null);

  const loadOwedComps = async () => {
    try {
      const { data } = await getOwedCompensations();
      setOwedComps(data || []);
    } catch (err) {
      /* non-critical panel */
    }
  };

  useEffect(() => { loadOwedComps(); }, []);

  const handleRetryComp = async (row) => {
    setRetryingComp(row.delivery_id);
    try {
      const { data } = await retryCompensationTransfer(row.delivery_id);
      if (data.transfer_status === 'paid') {
        toast.success(`€${Number(row.amount_eur).toFixed(2)} paid to ${row.driver_name}.`);
      } else {
        toast.warning(`Still not payable: ${data.transfer_error || data.transfer_status}`);
      }
      await loadOwedComps();
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Retry failed');
    } finally {
      setRetryingComp(null);
    }
  };

  const handleNudgeCustomer = async (delivery) => {
    setNudgingId(delivery.id);
    try {
      const { data } = await nudgeCustomer(delivery.id);
      toast.success(`Nudge sent to ${data.email} — ${data.active_bids} bid${data.active_bids === 1 ? '' : 's'} waiting.`);
      setDeliveries((prev) => prev.map((d) => (
        d.id === delivery.id
          ? { ...d, last_nudged_at: data.last_nudged_at, nudge_count: data.nudge_count }
          : d
      )));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send the nudge email');
    } finally {
      setNudgingId(null);
    }
  };


  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => {
      localStorage.setItem('admin_sidebar_collapsed', prev ? '0' : '1');
      return !prev;
    });
  };

  const section = ADMIN_NAV_LOOKUP[mainTab] || ADMIN_NAV_LOOKUP.deliveries;

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-dashboard">
      <Navbar />

      <Tabs value={mainTab} onValueChange={setMainTab} className="flex flex-col md:flex-row items-start w-full">
        {/* ── Desktop side navigation ── */}
        <aside
          className={`hidden md:flex flex-col flex-shrink-0 sticky top-16 h-[calc(100vh-4rem)] z-30 border-r border-slate-200 bg-white transition-[width] duration-300 ease-in-out ${sidebarCollapsed ? 'w-20' : 'w-64 lg:w-72'}`}
          data-testid="admin-sidebar"
        >
          <AdminSideNav
            supportUnread={supportUnread}
            collapsed={sidebarCollapsed}
            onToggleCollapse={toggleSidebar}
          />
        </aside>

        {/* ── Main work area ── */}
        <main className="flex-1 min-w-0 w-full max-w-[1600px] px-4 sm:px-6 lg:px-8 py-6 md:py-8">
          {/* One-shot enable-push banner for admins so they receive browser
              notifications for new signups, new deliveries, etc. */}
          <NotificationPrompt role="admin" />

          {/* Header */}
          <div className="mb-7 flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3 min-w-0">
              {/* Mobile: the same navigation in a drawer */}
              <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
                <SheetTrigger asChild>
                  <button
                    className="md:hidden mt-1 w-10 h-10 rounded-lg border border-slate-200 bg-white flex items-center justify-center text-slate-700 hover:bg-slate-100 transition-colors flex-shrink-0"
                    data-testid="admin-mobile-nav-trigger"
                  >
                    <Menu className="w-5 h-5" />
                  </button>
                </SheetTrigger>
                <SheetContent side="left" className="p-0 w-[280px] sm:w-[320px] bg-white">
                  <SheetTitle className="sr-only">Admin navigation</SheetTitle>
                  <AdminSideNav
                    supportUnread={supportUnread}
                    variant="mobile"
                    onNavigate={() => setMobileNavOpen(false)}
                  />
                </SheetContent>
              </Sheet>
              <div className="min-w-0">
                <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Operations Console</p>
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">{section.label}</h1>
                <p className="text-sm text-slate-500 mt-0.5">{section.description}</p>
              </div>
            </div>
            {/* Always-visible toggle so admins can re-enable/disable Web Push */}
            <NotificationToggle />
          </div>



          {/* =================== DELIVERIES TAB =================== */}
          <TabsContent value="deliveries">
            {/* Compensation captured but not yet transferred to the driver */}
            {owedComps.length > 0 && (
              <div className="mb-6 rounded-lg border-2 border-amber-300 bg-amber-50 p-4" data-testid="owed-compensations-panel">
                <p className="text-sm font-bold text-amber-900 flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4" /> Compensation owed to drivers ({owedComps.length})
                </p>
                <p className="text-xs text-amber-800 mb-3">
                  These amounts were captured from the customer but could not be transferred yet. They are retried automatically — you can also retry now.
                </p>
                <div className="space-y-2">
                  {owedComps.map((row) => (
                    <div key={row.id} className="flex flex-wrap items-center justify-between gap-2 bg-white border border-amber-200 rounded-md px-3 py-2" data-testid={`owed-comp-row-${row.delivery_id}`}>
                      <div className="text-xs">
                        <p className="font-semibold text-gray-900">
                          €{Number(row.amount_eur).toFixed(2)} → {row.driver_name || 'driver'}
                          <span className="text-gray-500 font-normal"> · {row.delivery_ref || row.delivery_id.slice(0, 8)}</span>
                        </p>
                        <p className="text-gray-500 mt-0.5">{row.failure_reason} · {row.attempts} attempt(s)</p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={retryingComp === row.delivery_id}
                        onClick={() => handleRetryComp(row)}
                        data-testid={`retry-comp-btn-${row.delivery_id}`}
                      >
                        {retryingComp === row.delivery_id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Retry payout'}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* Revenue Stats */}
            {revenue && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold font-['Inter']">Revenue Overview</h2>
                  <div className="flex items-center gap-2">
                    {revenue.period_label && (
                      <span className="text-sm text-gray-500">{revenue.period_label}</span>
                    )}
                    <select
                      data-testid="revenue-period-filter"
                      value={revenuePeriod}
                      onChange={(e) => setRevenuePeriod(e.target.value)}
                      className="border border-gray-300 rounded-md px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    >
                      <option value="today">Today</option>
                      <option value="this_week">This Week</option>
                      <option value="this_month">This Month</option>
                      <option value="this_year">This Year</option>
                      <option value="all_time">All time (since Sept 2026)</option>
                    </select>
                  </div>
                </div>
                <div className="mb-3 rounded-md border border-emerald-200 bg-emerald-50/60 px-3 py-2" data-testid="revenue-epoch-note">
                  <p className="text-xs font-semibold text-[#1B5E20]">Fresh start — {revenue.revenue_epoch_label || 'September 2026'}</p>
                  <p className="text-[11px] text-emerald-800/90 mt-0.5">
                    {revenue.revenue_note || 'Revenue tracking restarted when the Pay Driver Directly system went live. Bookings taken before then are excluded.'}
                  </p>
                </div>
                <p className="text-xs text-gray-500 mb-3">JustDeliver revenue = Booking Deposits + JustDeliver Driver Fees. Money paid directly to drivers is never JustDeliver revenue.</p>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                  <Card className="border-2 border-emerald-200 bg-emerald-50 shadow-none" data-testid="revenue-card-total">
                    <CardContent className="pt-5 pb-5">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                        <div className="w-10 h-10 sm:w-11 sm:h-11 bg-emerald-200 flex items-center justify-center rounded-md flex-shrink-0">
                          <Euro className="w-5 h-5 text-emerald-800" strokeWidth={1.5} />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xl sm:text-2xl font-bold text-emerald-800 leading-tight break-words" data-testid="revenue-total">€{revenue.total_revenue?.toFixed(2) || '0.00'}</p>
                          <p className="text-xs text-emerald-700 font-medium">JustDeliver Net Revenue</p>
                          {!!revenue.total_refunded && revenue.total_refunded > 0 && (
                            <p className="text-[10px] text-emerald-700/70">Gross €{revenue.gross_revenue?.toFixed(2)} − refunds</p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border-2 border-[#2E7D32]/30 bg-[#2E7D32]/5 shadow-none" data-testid="revenue-card-fees">
                    <CardContent className="pt-5 pb-5">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                        <div className="w-10 h-10 sm:w-11 sm:h-11 bg-[#2E7D32]/20 flex items-center justify-center rounded-md flex-shrink-0">
                          <Shield className="w-5 h-5 text-[#2E7D32]" strokeWidth={1.5} />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xl sm:text-2xl font-bold text-[#2E7D32] leading-tight break-words" data-testid="revenue-booking-deposits">€{revenue.total_booking_deposits?.toFixed(2) || '0.00'}</p>
                          <p className="text-xs text-[#1B5E20] font-medium">Booking Deposits (€10 each)</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border-2 border-blue-200 bg-blue-50/60 shadow-none" data-testid="revenue-card-driver-fees">
                    <CardContent className="pt-5 pb-5">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                        <div className="w-10 h-10 sm:w-11 sm:h-11 bg-blue-100 flex items-center justify-center rounded-md flex-shrink-0">
                          <Euro className="w-5 h-5 text-blue-700" strokeWidth={1.5} />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xl sm:text-2xl font-bold text-blue-900 leading-tight break-words" data-testid="revenue-driver-fees">€{revenue.total_driver_fees?.toFixed(2) || '0.00'}</p>
                          <p className="text-xs text-blue-800 font-medium">JustDeliver Driver Fees (10%)</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border border-gray-200 shadow-none" data-testid="revenue-card-payouts">
                    <CardContent className="pt-5 pb-5">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                        <div className="w-10 h-10 sm:w-11 sm:h-11 bg-gray-100 flex items-center justify-center rounded-md flex-shrink-0">
                          <Users className="w-5 h-5 text-gray-600" strokeWidth={1.5} />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xl sm:text-2xl font-bold leading-tight break-words" data-testid="revenue-driver-amounts">€{revenue.total_driver_payouts?.toFixed(2) || '0.00'}</p>
                          <p className="text-xs text-gray-600 font-medium">Paid to Drivers (not revenue)</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border border-gray-200 shadow-none" data-testid="revenue-card-transactions">
                    <CardContent className="pt-5 pb-5">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                        <div className="w-10 h-10 sm:w-11 sm:h-11 bg-gray-100 flex items-center justify-center rounded-md flex-shrink-0">
                          <CreditCard className="w-5 h-5 text-gray-600" strokeWidth={1.5} />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xl sm:text-2xl font-bold leading-tight">{revenue.total_transactions || 0}</p>
                          <p className="text-xs text-gray-600 font-medium">Paid Deliveries</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className={`border shadow-none ${(revenue.total_refunded || 0) > 0 ? 'border-purple-200 bg-purple-50' : 'border-gray-200'}`} data-testid="revenue-card-refunded">
                    <CardContent className="pt-5 pb-5">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                        <div className={`w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center rounded-md flex-shrink-0 ${(revenue.total_refunded || 0) > 0 ? 'bg-purple-200' : 'bg-gray-100'}`}>
                          <AlertTriangle className={`w-5 h-5 ${(revenue.total_refunded || 0) > 0 ? 'text-purple-800' : 'text-gray-600'}`} strokeWidth={1.5} />
                        </div>
                        <div className="min-w-0">
                          <p className={`text-xl sm:text-2xl font-bold leading-tight break-words ${(revenue.total_refunded || 0) > 0 ? 'text-purple-800' : ''}`}>€{revenue.total_refunded?.toFixed(2) || '0.00'}</p>
                          <p className={`text-xs font-medium ${(revenue.total_refunded || 0) > 0 ? 'text-purple-700' : 'text-gray-600'}`}>Refunded</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {/* Fee Structure Info — Booking Deposit + JustDeliver Driver Fee */}
            <Card className="border border-gray-200 shadow-none mb-6">
              <CardContent className="py-5">
                <div className="flex items-center gap-2 mb-4">
                  <Shield className="w-5 h-5 text-[#2E7D32]" />
                  <span className="font-semibold text-base">JustDeliver Fee Structure</span>
                  <span className="ml-1 text-xs text-gray-500">(max delivery value €1,200)</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Customer side */}
                  <div className="rounded-md border border-emerald-100 bg-emerald-50/40 p-3" data-testid="fee-table-customer">
                    <p className="text-xs font-bold uppercase tracking-wider text-[#1B5E20] mb-2">
                      Customer — Booking Deposit
                    </p>
                    <p className="text-[11px] text-gray-500 mb-3">Paid online to JustDeliver, on top of the driver&apos;s bid.</p>
                    <ul className="text-sm space-y-1.5">
                      <li className="flex justify-between"><span>Every booking (€1–€1,200)</span><span className="font-semibold">€10 flat</span></li>
                    </ul>
                  </div>
                  {/* Driver side */}
                  <div className="rounded-md border border-blue-100 bg-blue-50/40 p-3" data-testid="fee-table-driver">
                    <p className="text-xs font-bold uppercase tracking-wider text-[#0D47A1] mb-2">
                      JustDeliver Driver Fee
                    </p>
                    <p className="text-[11px] text-gray-500 mb-3">Paid online by the customer and taken off the amount they pay the driver directly.</p>
                    <ul className="text-sm space-y-1.5">
                      <li className="flex justify-between"><span>Bids €1–€99.99</span><span className="font-semibold">€0</span></li>
                      <li className="flex justify-between"><span>Bids €100–€1,200</span><span className="font-semibold">10% of the accepted bid</span></li>
                    </ul>
                  </div>
                </div>
                <p className="text-[11px] text-gray-500 mt-3">
                  Example: driver bids €95 → customer pays €10 online + €95 directly to the driver (€105 total), JustDeliver revenue €10. Driver bids €150 → customer pays €25 online (€10 + €15) + €135 directly (€160 total), JustDeliver revenue €25. Driver bids €1,200 → customer pays €130 online + €1,080 directly (€1,210 total), JustDeliver revenue €130.
                </p>
              </CardContent>
            </Card>

            {/* Stats Cards */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
                <Card className="border border-gray-200 shadow-none">
                  <CardContent className="pt-6 text-center">
                    <Package className="w-8 h-8 mx-auto text-gray-400 mb-2" strokeWidth={1.5} />
                    <p className="text-2xl font-bold">{stats.total_deliveries}</p>
                    <p className="text-xs text-gray-600 uppercase tracking-wider">Total Jobs</p>
                  </CardContent>
                </Card>
                <Card className="border border-gray-200 shadow-none">
                  <CardContent className="pt-6 text-center">
                    <Clock className="w-8 h-8 mx-auto text-amber-500 mb-2" strokeWidth={1.5} />
                    <p className="text-2xl font-bold">{stats.pending}</p>
                    <p className="text-xs text-gray-600 uppercase tracking-wider">Pending</p>
                  </CardContent>
                </Card>
                <Card className="border border-gray-200 shadow-none">
                  <CardContent className="pt-6 text-center">
                    <TrendingUp className="w-8 h-8 mx-auto text-blue-500 mb-2" strokeWidth={1.5} />
                    <p className="text-2xl font-bold">{stats.assigned}</p>
                    <p className="text-xs text-gray-600 uppercase tracking-wider">In Progress</p>
                  </CardContent>
                </Card>
                <Card className="border border-gray-200 shadow-none">
                  <CardContent className="pt-6 text-center">
                    <Check className="w-8 h-8 mx-auto text-emerald-500 mb-2" strokeWidth={1.5} />
                    <p className="text-2xl font-bold">{stats.completed}</p>
                    <p className="text-xs text-gray-600 uppercase tracking-wider">Completed</p>
                  </CardContent>
                </Card>
                <Card className="border border-gray-200 shadow-none">
                  <CardContent className="pt-6 text-center">
                    <Users className="w-8 h-8 mx-auto text-purple-500 mb-2" strokeWidth={1.5} />
                    <p className="text-2xl font-bold">{stats.total_drivers}</p>
                    <p className="text-xs text-gray-600 uppercase tracking-wider">Drivers</p>
                  </CardContent>
                </Card>
                <Card className="border border-gray-200 shadow-none">
                  <CardContent className="pt-6 text-center">
                    <Users className="w-8 h-8 mx-auto text-indigo-500 mb-2" strokeWidth={1.5} />
                    <p className="text-2xl font-bold">{stats.total_customers}</p>
                    <p className="text-xs text-gray-600 uppercase tracking-wider">Customers</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Search + Period Filter */}
            <div className="flex gap-2 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by delivery ID (JD-2026-...), location, customer, item..."
                  value={deliverySearch}
                  onChange={(e) => { setDeliverySearch(e.target.value); setAdminDeliveryPage(1); }}
                  className="w-full border border-gray-200 rounded-md pl-10 pr-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  data-testid="delivery-search-input"
                />
              </div>
              <select
                value={deliveryPeriod}
                onChange={(e) => { setDeliveryPeriod(e.target.value); setAdminDeliveryPage(1); }}
                className="border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                data-testid="delivery-period-filter"
              >
                <option value="today">Today</option>
                <option value="this_week">This Week</option>
                <option value="this_month">This Month</option>
                <option value="this_year">This Year</option>
                <option value="all_time">All Time</option>
              </select>
            </div>
            <Tabs defaultValue="all" className="space-y-6" onValueChange={() => setAdminDeliveryPage(1)}>
              <TabsList className="bg-gray-100 p-1">
                <TabsTrigger value="all" className="data-[state=active]:bg-white" data-testid="tab-all">
                  All ({deliveries.length})
                </TabsTrigger>
                <TabsTrigger value="pending" className="data-[state=active]:bg-white" data-testid="tab-pending">
                  Pending ({filterDeliveries('pending').length})
                </TabsTrigger>
                <TabsTrigger value="assigned" className="data-[state=active]:bg-white" data-testid="tab-assigned">
                  In Progress ({filterDeliveries('assigned').length})
                </TabsTrigger>
                <TabsTrigger value="completed" className="data-[state=active]:bg-white" data-testid="tab-completed">
                  Completed ({filterDeliveries('completed').length})
                </TabsTrigger>
                <TabsTrigger value="expired" className="data-[state=active]:bg-white gap-1.5" data-testid="tab-expired">
                  <Hourglass className="w-3.5 h-3.5" />
                  Expired
                  {filterDeliveries('expired').length > 0 && (
                    <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-[18px] px-1 rounded-full bg-red-100 text-red-700 text-[10px] font-bold" data-testid="admin-tab-expired-count">
                      {filterDeliveries('expired').length}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger value="cancelled" className="data-[state=active]:bg-white gap-1.5" data-testid="tab-cancelled">
                  <X className="w-3.5 h-3.5" />
                  Cancelled
                  {filterDeliveries('cancelled').length > 0 && (
                    <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-[18px] px-1 rounded-full bg-orange-100 text-orange-700 text-[10px] font-bold" data-testid="admin-tab-cancelled-count">
                      {filterDeliveries('cancelled').length}
                    </span>
                  )}
                </TabsTrigger>
              </TabsList>

              {selectedDeliveryIds.length > 0 && (
                <div className="mt-3">
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setBulkDeleteDeliveriesOpen(true)}
                    className="gap-1"
                    data-testid="bulk-delete-deliveries-btn"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Delete Selected ({selectedDeliveryIds.length})
                  </Button>
                </div>
              )}

              {['all', 'pending', 'assigned', 'completed', 'expired', 'cancelled'].map((status) => (
                <TabsContent key={status} value={status}>
                  {status === 'expired' && !loading && filterDeliveries('expired').length > 0 && (
                    <div className="mb-3 px-3 py-2 rounded-md bg-red-50 border border-red-100 text-xs text-red-700 flex items-start gap-2" data-testid="admin-expired-helper">
                      <Hourglass className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>Archived delivery requests that expired without a driver being selected (same-day cap: 3h, future-dated: 24h). Customer can repost any of these — nothing was charged.</span>
                    </div>
                  )}
                  {status === 'cancelled' && !loading && filterDeliveries('cancelled').length > 0 && (
                    <div className="mb-3 px-3 py-2 rounded-md bg-orange-50 border border-orange-100 text-xs text-orange-700 flex items-start gap-2" data-testid="admin-cancelled-helper">
                      <X className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>Deliveries the customer cancelled before payment was authorised. The original cancellation reason is stored on the delivery and visible in the details panel.</span>
                    </div>
                  )}
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <Package className="w-8 h-8 text-gray-400 animate-pulse" />
                    </div>
                  ) : filterDeliveries(status).length === 0 ? (
                    <Card className="border border-gray-200 shadow-none">
                      <CardContent className="flex flex-col items-center justify-center py-12">
                        <Package className="w-16 h-16 text-gray-300 mb-4" strokeWidth={1} />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No deliveries found</h3>
                        <p className="text-gray-600">No {status === 'all' ? '' : status} deliveries at the moment</p>
                      </CardContent>
                    </Card>
                  ) : (() => {
                    const allFiltered = filterDeliveries(status);
                    const ITEMS_PER_PAGE = 10;
                    const totalPages = Math.ceil(allFiltered.length / ITEMS_PER_PAGE);
                    const paged = allFiltered.slice((adminDeliveryPage - 1) * ITEMS_PER_PAGE, adminDeliveryPage * ITEMS_PER_PAGE);
                    return (
                    <>
                    <Card className="border border-gray-200 shadow-none overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-50 hover:bg-gray-50">
                            <TableHead className="w-10">
                              <input
                                type="checkbox"
                                checked={paged.length > 0 && paged.every(d => selectedDeliveryIds.includes(d.id))}
                                onChange={toggleSelectAllDeliveries}
                                className="w-4 h-4 rounded border-gray-300 accent-[#2E7D32] cursor-pointer"
                                data-testid="select-all-deliveries"
                              />
                            </TableHead>
                            <TableHead className="font-semibold text-gray-900">Delivery&nbsp;ID</TableHead>
                            <TableHead className="font-semibold text-gray-900">Customer</TableHead>
                            <TableHead className="font-semibold text-gray-900">Route</TableHead>
                            <TableHead className="font-semibold text-gray-900">Item</TableHead>
                            <TableHead className="font-semibold text-gray-900">Submitted</TableHead>
                            <TableHead className="font-semibold text-gray-900">Status</TableHead>
                            <TableHead className="font-semibold text-gray-900">Driver</TableHead>
                            <TableHead className="font-semibold text-gray-900">Amount</TableHead>
                            <TableHead className="font-semibold text-gray-900 text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {paged.map((delivery) => (
                            <TableRow key={delivery.id} className={`hover:bg-gray-50 ${selectedDeliveryIds.includes(delivery.id) ? 'bg-blue-50/50' : ''}`} data-testid={`admin-delivery-row-${delivery.id}`}>
                              <TableCell>
                                <input
                                  type="checkbox"
                                  checked={selectedDeliveryIds.includes(delivery.id)}
                                  onChange={() => toggleSelectDelivery(delivery.id)}
                                  className="w-4 h-4 rounded border-gray-300 accent-[#2E7D32] cursor-pointer"
                                  data-testid={`select-delivery-${delivery.id}`}
                                />
                              </TableCell>
                              <TableCell>
                                {delivery.delivery_ref ? (
                                  <span className="inline-block font-mono text-[11px] font-bold text-[#1B5E20] bg-emerald-50 border border-emerald-100 px-2 py-1 rounded" data-testid={`admin-delivery-ref-${delivery.id}`}>
                                    {delivery.delivery_ref}
                                  </span>
                                ) : (
                                  <span className="text-xs text-gray-300">—</span>
                                )}
                              </TableCell>
                              <TableCell>
                                <div>
                                  <p className="font-medium text-sm">{delivery.customer_name}</p>
                                  <p className="text-xs text-gray-400">{delivery.customer_phone}</p>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="text-sm">
                                  <p className="font-medium">{delivery.pickup_county || delivery.pickup_location?.split(',').pop()?.trim()} → {delivery.dropoff_county || delivery.dropoff_location?.split(',').pop()?.trim()}</p>
                                  <p className="text-xs text-gray-400 truncate max-w-[180px]" title={delivery.pickup_location}>{delivery.pickup_location}</p>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="text-xs text-gray-600">
                                  <p className="truncate max-w-[100px] font-medium" title={delivery.item_description}>{delivery.item_description}</p>
                                  {delivery.item_type && <p className="text-gray-400 capitalize">{delivery.item_type}</p>}
                                </div>
                              </TableCell>
                              <TableCell className="text-xs text-gray-500 whitespace-nowrap">
                                {delivery.created_at ? new Date(delivery.created_at).toLocaleDateString('en-IE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-col gap-1">
                                  <StatusBadge status={delivery.status} />
                                  {/* Capture-status chip */}
                                  {delivery.capture_status === 'pending_delivery' && (
                                    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-amber-50 border border-amber-200 text-amber-800 rounded-full" title="Auth on card. Capture happens 24h after delivery.">
                                      <Clock className="w-3 h-3" /> Auth held
                                    </span>
                                  )}
                                  {delivery.capture_status === 'scheduled' && (
                                    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-blue-50 border border-blue-200 text-blue-800 rounded-full" title={`Capture at ${delivery.capture_at ? new Date(delivery.capture_at).toLocaleString() : 'scheduled'}`}>
                                      <Clock className="w-3 h-3" /> Capture in 24h
                                    </span>
                                  )}
                                  {delivery.capture_status === 'captured' && (
                                    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-full">
                                      <CheckCircle className="w-3 h-3" /> Captured
                                    </span>
                                  )}
                                  {delivery.capture_status === 'cancelled' && (
                                    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-gray-100 border border-gray-200 text-gray-700 rounded-full">
                                      <Ban className="w-3 h-3" /> Cancelled
                                    </span>
                                  )}
                                  {(delivery.payment_status === 'refunded' || delivery.payment_status === 'partially_refunded') && (
                                    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-purple-50 border border-purple-200 text-purple-800 rounded-full">
                                      {delivery.payment_status === 'refunded' ? 'Refunded' : 'Part. refunded'}
                                    </span>
                                  )}
                                  {delivery.payment_status === 'paid' && !delivery.capture_status && (
                                    <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
                                      <CheckCircle className="w-3 h-3" /> Paid
                                    </span>
                                  )}
                                  {/* 6-digit verification code — always visible to admin */}
                                  {delivery.delivery_code && (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        try { navigator.clipboard.writeText(delivery.delivery_code); toast.success(`Code ${delivery.delivery_code} copied`); } catch { /* clipboard unavailable */ }
                                      }}
                                      className="group inline-flex items-center gap-1 text-[11px] font-mono font-bold px-1.5 py-0.5 bg-[#2E7D32]/10 border border-[#2E7D32]/30 text-[#1B5E20] rounded hover:bg-[#2E7D32]/15"
                                      title="6-digit verification code — click to copy"
                                      data-testid={`admin-delivery-code-${delivery.id}`}
                                    >
                                      <KeyRound className="w-3 h-3" /> {delivery.delivery_code}
                                      <Copy className="w-2.5 h-2.5 opacity-0 group-hover:opacity-100" />
                                    </button>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                {delivery.assigned_driver_name ? (
                                  <p className="font-medium text-sm">{delivery.assigned_driver_name}</p>
                                ) : <span className="text-gray-400">—</span>}
                              </TableCell>
                              <TableCell>
                                {delivery.assigned_price ? (
                                  <div>
                                    <span className="font-semibold">€{delivery.assigned_price.toFixed(2)}</span>
                                    {delivery.platform_fee && <p className="text-[10px] text-[#2E7D32]">Deposit: €{delivery.platform_fee.toFixed(2)}</p>}
                                  </div>
                                ) : <span className="text-gray-400">—</span>}
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-1">
                                <Dialog open={dialogOpen && selectedDelivery?.id === delivery.id} onOpenChange={(open) => {
                                  if (!open) setDialogOpen(false);
                                }}>
                                  <DialogTrigger asChild>
                                    <Button variant="ghost" size="sm" onClick={() => viewOffers(delivery)} className="gap-1 hover:bg-gray-100" data-testid={`view-offers-admin-btn-${delivery.id}`}>
                                      <Eye className="w-4 h-4" strokeWidth={1.5} /> Details
                                    </Button>
                                  </DialogTrigger>
                                  <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                                    <DialogHeader>
                                      <DialogTitle className="font-['Inter']">Delivery Details</DialogTitle>
                                      {selectedDelivery?.delivery_ref && (
                                        <p className="mt-1 inline-block font-mono text-xs font-bold text-[#1B5E20] bg-emerald-50 border border-emerald-100 px-2 py-1 rounded w-fit" data-testid="admin-delivery-detail-ref">
                                          {selectedDelivery.delivery_ref}
                                        </p>
                                      )}
                                    </DialogHeader>
                                    <div className="bg-gray-50 p-4 border border-gray-200 mb-4 space-y-3 text-sm">
                                      {/* 6-digit verification code — always shown to admin for support */}
                                      {selectedDelivery?.delivery_code && (
                                        <div className="flex items-center justify-between gap-3 -m-1 mb-1 p-3 bg-[#2E7D32]/5 border border-[#2E7D32]/20 rounded-md" data-testid="admin-detail-code">
                                          <div>
                                            <p className="text-[10px] uppercase tracking-wider text-[#1B5E20] font-bold flex items-center gap-1">
                                              <KeyRound className="w-3 h-3" /> Verification code
                                            </p>
                                            <p className="font-mono text-2xl font-bold text-[#1B5E20] tracking-widest mt-0.5">{selectedDelivery.delivery_code}</p>
                                            <p className="text-[11px] text-gray-500 mt-0.5">Customer shares this with the driver to confirm delivery.</p>
                                          </div>
                                          <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => { try { navigator.clipboard.writeText(selectedDelivery.delivery_code); toast.success('Code copied'); } catch { /* clipboard unavailable */ } }}
                                            className="border-[#2E7D32]/40 text-[#1B5E20] hover:bg-[#2E7D32]/10 gap-1.5 rounded-sm"
                                            data-testid="admin-detail-code-copy-btn"
                                          >
                                            <Copy className="w-3.5 h-3.5" /> Copy
                                          </Button>
                                        </div>
                                      )}
                                      <div>
                                        <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">Pickup Address</p>
                                        <p className="font-medium">{selectedDelivery?.pickup_location}</p>
                                        <div className="flex gap-3 text-xs text-gray-500 mt-0.5">
                                          {selectedDelivery?.pickup_county && <span>{selectedDelivery.pickup_county}</span>}
                                          {selectedDelivery?.pickup_eircode && <span>{selectedDelivery.pickup_eircode}</span>}
                                        </div>
                                      </div>
                                      <div>
                                        <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">Drop-off Address</p>
                                        <p className="font-medium">{selectedDelivery?.dropoff_location}</p>
                                        <div className="flex gap-3 text-xs text-gray-500 mt-0.5">
                                          {selectedDelivery?.dropoff_county && <span>{selectedDelivery.dropoff_county}</span>}
                                          {selectedDelivery?.dropoff_eircode && <span>{selectedDelivery.dropoff_eircode}</span>}
                                        </div>
                                      </div>
                                      <div className="grid grid-cols-2 gap-3 pt-2 border-t border-gray-200">
                                        <div>
                                          <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">Item</p>
                                          <p>{selectedDelivery?.item_description}</p>
                                          {selectedDelivery?.item_type && <p className="text-xs text-gray-400 capitalize">{selectedDelivery.item_type}</p>}
                                        </div>
                                        <div>
                                          <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">Delivery Window</p>
                                          <p>{selectedDelivery?.preferred_time ? formatDate(selectedDelivery.preferred_time) : '—'}</p>
                                        </div>
                                      </div>
                                      <div className="grid grid-cols-2 gap-3 pt-2 border-t border-gray-200">
                                        <div>
                                          <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">Submitted</p>
                                          <p>{selectedDelivery?.created_at ? new Date(selectedDelivery.created_at).toLocaleDateString('en-IE', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}</p>
                                        </div>
                                        <div>
                                          <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">Customer</p>
                                          <p>{selectedDelivery?.customer_name}</p>
                                          <p className="text-xs text-gray-400">{selectedDelivery?.customer_phone}</p>
                                        </div>
                                      </div>
                                      <div className="grid grid-cols-2 gap-3 pt-2 border-t border-gray-200">
                                        {selectedDelivery?.receiver_name && (
                                          <div>
                                            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">Receiver</p>
                                            <p>{selectedDelivery.receiver_name}</p>
                                            {selectedDelivery.receiver_phone && <p className="text-xs text-gray-400">{selectedDelivery.receiver_phone}</p>}
                                          </div>
                                        )}
                                        <div>
                                          <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">Dimensions / Weight</p>
                                          <p>
                                            {selectedDelivery?.dimensions_length ? `${selectedDelivery.dimensions_length}x${selectedDelivery.dimensions_width || '?'}x${selectedDelivery.dimensions_height || '?'} cm` : '—'}
                                            {selectedDelivery?.weight_kg ? ` / ${selectedDelivery.weight_kg}kg` : ''}
                                          </p>
                                        </div>
                                        <div>
                                          <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">Customer Budget</p>
                                          <p className="font-semibold" data-testid="admin-customer-budget">
                                            {selectedDelivery?.customer_budget != null
                                              ? <>💶 €{Number(selectedDelivery.customer_budget).toFixed(2)} <span className="text-xs text-gray-400 font-normal">(Guide)</span></>
                                              : <span className="text-gray-400">Not specified</span>}
                                          </p>
                                        </div>
                                      </div>
                                      {selectedDelivery?.assigned_price && (
                                        <div className="pt-2 border-t border-gray-200 grid grid-cols-3 gap-3">
                                          <div>
                                            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">Total</p>
                                            <p className="font-bold text-lg">€{selectedDelivery.assigned_price.toFixed(2)}</p>
                                          </div>
                                          <div>
                                            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">Platform Fee</p>
                                            <p className="font-semibold text-[#2E7D32]">€{selectedDelivery.platform_fee?.toFixed(2) || '—'}</p>
                                          </div>
                                          <div>
                                            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">Driver Payout</p>
                                            <p className="font-semibold">€{selectedDelivery.driver_payout?.toFixed(2) || '—'}</p>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                    <h4 className="font-semibold text-sm mb-2">Driver Offers</h4>
                                    {offersLoading ? (
                                      <div className="flex items-center justify-center py-8">
                                        <Package className="w-6 h-6 text-gray-400 animate-pulse" />
                                      </div>
                                    ) : offers.length === 0 ? (
                                      <div className="text-center py-8 text-gray-600">No offers yet for this delivery</div>
                                    ) : (
                                      <div className="space-y-2">
                                        {offers.map((offer) => (
                                          <div key={offer.id} className={`p-3 border text-sm ${selectedDelivery?.assigned_driver_id === offer.driver_id ? 'border-[#2E7D32] bg-[#2E7D32]/5' : 'border-gray-200'}`}>
                                            <div className="flex justify-between items-center">
                                              <div>
                                                <div className="flex items-center gap-2">
                                                  <span className="font-medium">{offer.driver_name}</span>
                                                  {selectedDelivery?.assigned_driver_id === offer.driver_id && <span className="px-2 py-0.5 bg-[#2E7D32] text-white text-xs rounded">Selected</span>}
                                                </div>
                                                <span className="text-gray-500">{offer.driver_phone}</span>
                                              </div>
                                              <div className="text-right">
                                                <p className="font-bold">€{offer.price.toFixed(2)}</p>
                                                <p className="text-xs text-gray-500">Deposit: €{offer.platform_fee.toFixed(2)}</p>
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    )}

                                    {/* Customer cancellation reason — visible only on cancelled deliveries */}
                                    {selectedDelivery?.status === 'cancelled' && (
                                      <div className="mt-5 p-3 border border-orange-200 rounded-md bg-orange-50" data-testid="admin-cancellation-reason">
                                        <h4 className="font-semibold text-sm mb-1 flex items-center gap-1.5 text-orange-800">
                                          <X className="w-4 h-4" /> Cancellation reason
                                        </h4>
                                        <p className="text-sm text-orange-900">{selectedDelivery.cancellation_reason_label || 'No reason recorded.'}</p>
                                        {selectedDelivery.cancelled_at && (
                                          <p className="text-[11px] text-orange-700 mt-1">
                                            Cancelled {new Date(selectedDelivery.cancelled_at).toLocaleString()}
                                          </p>
                                        )}
                                      </div>
                                    )}

                                    {/* Customer item photos */}
                                    {selectedDelivery?.photos && selectedDelivery.photos.length > 0 && (
                                      <div className="mt-5" data-testid="admin-customer-photos">
                                        <h4 className="font-semibold text-sm mb-2 flex items-center gap-1.5">
                                          <Camera className="w-4 h-4 text-[#2E7D32]" /> Customer item photos ({selectedDelivery.photos.length})
                                        </h4>
                                        <div className="grid grid-cols-3 gap-2">
                                          {selectedDelivery.photos.map((url, i) => (
                                            <a
                                              key={i}
                                              href={resolveAssetUrl(url)}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="block border border-gray-200 rounded overflow-hidden hover:opacity-90 transition-opacity"
                                              data-testid={`admin-customer-photo-${i}`}
                                              title="Click to open full size"
                                            >
                                              <img
                                                src={resolveAssetUrl(url)}
                                                alt={`Item ${i + 1}`}
                                                className="w-full h-24 object-cover"
                                                loading="lazy"
                                              />
                                            </a>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {/* Driver proof-of-delivery photo */}
                                    {selectedDelivery?.delivery_proof_photo && (
                                      <div className="mt-5" data-testid="admin-proof-photo">
                                        <h4 className="font-semibold text-sm mb-2 flex items-center gap-1.5">
                                          <CheckCircle className="w-4 h-4 text-emerald-600" /> Driver proof of delivery
                                        </h4>
                                        <a
                                          href={resolveAssetUrl(selectedDelivery.delivery_proof_photo)}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="block border border-emerald-200 rounded overflow-hidden bg-emerald-50/40 hover:opacity-90 transition-opacity max-w-xs"
                                          title="Click to open full size"
                                        >
                                          <img
                                            src={resolveAssetUrl(selectedDelivery.delivery_proof_photo)}
                                            alt="Proof of delivery"
                                            className="w-full h-auto"
                                            loading="lazy"
                                          />
                                        </a>
                                      </div>
                                    )}
                                  </DialogContent>
                                </Dialog>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setDeleteDeliveryId(delivery.id)}
                                  className="gap-1 text-red-600 hover:bg-red-50 hover:text-red-700"
                                  data-testid={`delete-delivery-btn-${delivery.id}`}
                                >
                                  <Trash2 className="w-4 h-4" /> Delete
                                </Button>
                                {/* Capture lifecycle actions */}
                                {(delivery.capture_status === 'pending_delivery' || delivery.capture_status === 'scheduled') && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleCancelAuth(delivery)}
                                    className="gap-1 text-gray-700 hover:bg-gray-100"
                                    data-testid={`cancel-auth-btn-${delivery.id}`}
                                    title="Void the authorization — no funds move"
                                  >
                                    <Ban className="w-4 h-4" /> Cancel
                                  </Button>
                                )}
                                {(delivery.capture_status === 'pending_delivery' || delivery.capture_status === 'scheduled')
                                  && (delivery.payment_method || 'stripe') !== 'direct' && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setCompDelivery(delivery)}
                                    className="gap-1 text-red-700 hover:bg-red-50"
                                    data-testid={`cancel-compensate-btn-${delivery.id}`}
                                    title="Cancel and manually award the driver part of the held funds"
                                  >
                                    <HandCoins className="w-4 h-4" /> Cancel + Compensate
                                  </Button>
                                )}
                                {(delivery.payment_model === 'pay_driver_directly' || delivery.assigned_price > 0) && (() => {
                                  const bid = Number(delivery.agreed_bid_amount ?? delivery.assigned_price ?? 0);
                                  const deposit = Number(delivery.booking_deposit_amount ?? delivery.platform_fee ?? 0);
                                  // Older rows pre-date the stored fee fields — fall back to the
                                  // current 10% rule so revenue matches the backend calculation.
                                  const driverFee = Number(
                                    delivery.driver_deduction_amount
                                    ?? delivery.driver_access_fee
                                    ?? (bid >= 100 ? +(bid * 0.1).toFixed(2) : 0)
                                  );
                                  const paidDirect = Number(delivery.driver_payout_amount ?? delivery.direct_balance_due ?? (bid - driverFee));
                                  const jdRevenue = deposit + driverFee;
                                  const isLegacy = delivery.payment_model !== 'pay_driver_directly';
                                  return (
                                  <div className="text-[10px] leading-snug text-gray-600 bg-gray-50 border border-gray-200 rounded px-2 py-1 max-w-[250px]" data-testid={`pdd-summary-${delivery.id}`}>
                                    <div className="flex justify-between"><span>Driver Bid</span><strong>€{bid.toFixed(2)}</strong></div>
                                    <div className="flex justify-between"><span>Booking Deposit</span><strong>€{deposit.toFixed(2)}</strong></div>
                                    <div className="flex justify-between"><span>JustDeliver Driver Fee (10%)</span><strong>€{driverFee.toFixed(2)}</strong></div>
                                    <div className="flex justify-between border-t border-gray-200 mt-0.5 pt-0.5 text-[#1B5E20]"><span>Total JustDeliver Revenue</span><strong data-testid={`jd-revenue-${delivery.id}`}>€{jdRevenue.toFixed(2)}</strong></div>
                                    <div className="flex justify-between"><span>{isLegacy ? 'Paid to driver (earlier model)' : 'Paid directly to driver'}</span><strong>€{paidDirect.toFixed(2)}</strong></div>
                                    {delivery.driver_payment_methods?.length > 0 && (
                                      <div>Accepts: {delivery.driver_payment_methods.map(m => DIRECT_METHOD_LABELS[m] || m).join(', ')}</div>
                                    )}
                                    {delivery.cancelled_by && (
                                      <div className="mt-0.5 text-red-700">
                                        Cancelled by {delivery.cancelled_by}
                                        {delivery.cancellation_cycle ? ` (customer cancellation #${delivery.cancellation_cycle})` : ''} ·
                                        {delivery.refund_amount > 0
                                          ? ` refunded €${Number(delivery.refund_amount).toFixed(2)} (deposit + deduction)`
                                          : ' no refund'}
                                      </div>
                                    )}
                                    {delivery.cancellation_reason && (
                                      <div className="text-gray-500 italic">"{delivery.cancellation_reason}"</div>
                                    )}
                                    {delivery.direct_payment_method && (
                                      <div data-testid={`pdd-direct-method-${delivery.id}`}>
                                        Agreed payment method: {DIRECT_METHOD_LABELS[delivery.direct_payment_method] || delivery.direct_payment_method}
                                      </div>
                                    )}
                                  </div>
                                  );
                                })()}
                                {delivery.compensation && (
                                  <span
                                    className="text-[10px] font-semibold text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1 whitespace-nowrap"
                                    title={`${delivery.compensation.reason || ''} — released €${Number(delivery.compensation.released_eur || 0).toFixed(2)} to customer, transfer: ${delivery.compensation.transfer_status}`}
                                    data-testid={`compensation-badge-${delivery.id}`}
                                  >
                                    Cancelled — €{Number(delivery.compensation.compensation_eur || 0).toFixed(2)} compensated
                                    {delivery.compensation.transfer_status && delivery.compensation.transfer_status !== 'paid' && delivery.compensation.transfer_status !== 'not_required'
                                      ? ' (payout queued)' : ''}
                                  </span>
                                )}
                                {delivery.capture_status === 'scheduled' && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleForceCapture(delivery)}
                                    className="gap-1 text-blue-700 hover:bg-blue-50"
                                    data-testid={`force-capture-btn-${delivery.id}`}
                                    title="Skip the 24h window and capture now"
                                  >
                                    <CheckCircle className="w-4 h-4" /> Capture now
                                  </Button>
                                )}
                                {delivery.capture_status === 'captured' && delivery.payment_status !== 'refunded' && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleRefund(delivery)}
                                    className="gap-1 text-purple-700 hover:bg-purple-50"
                                    data-testid={`refund-btn-${delivery.id}`}
                                    title="Refund full or partial — driver transfer reversed proportionally"
                                  >
                                    <Euro className="w-4 h-4" /> Refund
                                  </Button>
                                )}
                                {/* 👋 Nudge customer — only when driver bids are waiting on them */}
                                {delivery.can_nudge && (
                                  <div className="flex flex-col items-start gap-0.5">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleNudgeCustomer(delivery)}
                                      disabled={nudgingId === delivery.id}
                                      className="gap-1 text-amber-700 hover:bg-amber-50 hover:text-amber-800 disabled:opacity-60"
                                      data-testid={`nudge-customer-btn-${delivery.id}`}
                                      title={`Email the customer a reminder — ${delivery.active_bid_count} bid(s) waiting`}
                                    >
                                      {nudgingId === delivery.id ? (
                                        <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>
                                      ) : (
                                        <><span aria-hidden="true">👋</span> Nudge Customer{delivery.active_bid_count ? ` (${delivery.active_bid_count})` : ''}</>
                                      )}
                                    </Button>
                                    {delivery.last_nudged_at && (
                                      <span
                                        className="text-[10px] font-medium text-emerald-700 pl-2 whitespace-nowrap"
                                        data-testid={`nudge-sent-at-${delivery.id}`}
                                      >
                                        ✓ Nudge Sent — {new Date(delivery.last_nudged_at).toLocaleString('en-IE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                        {delivery.nudge_count > 1 ? ` (×${delivery.nudge_count})` : ''}
                                      </span>
                                    )}
                                  </div>
                                )}
                                {delivery.capture_status === 'captured' && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleResendReceipt(delivery)}
                                    className="gap-1 text-emerald-700 hover:bg-emerald-50"
                                    data-testid={`resend-receipt-btn-${delivery.id}`}
                                    title="Resend the email receipt to the customer"
                                  >
                                    <Mail className="w-4 h-4" /> Resend receipt
                                  </Button>
                                )}
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Card>
                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-between mt-3 px-1">
                        <p className="text-xs text-gray-500">{allFiltered.length} deliveries • Page {adminDeliveryPage} of {totalPages}</p>
                        <div className="flex items-center gap-1">
                          <button onClick={() => setAdminDeliveryPage(p => Math.max(1, p - 1))} disabled={adminDeliveryPage === 1} className="px-2.5 py-1 text-xs font-medium rounded border border-gray-200 disabled:opacity-30 hover:bg-gray-50">Prev</button>
                          {Array.from({ length: totalPages }, (_, i) => i + 1)
                            .filter(p => p === 1 || p === totalPages || Math.abs(p - adminDeliveryPage) <= 2)
                            .reduce((acc, p, i, arr) => {
                              if (i > 0 && p - arr[i - 1] > 1) acc.push('...');
                              acc.push(p);
                              return acc;
                            }, [])
                            .map((p, i) => p === '...' ? (
                              <span key={`ellipsis-${i}`} className="px-1 text-xs text-gray-400">...</span>
                            ) : (
                              <button key={p} onClick={() => setAdminDeliveryPage(p)} className={`w-7 h-7 text-xs font-medium rounded ${p === adminDeliveryPage ? 'bg-[#2E7D32] text-white' : 'border border-gray-200 hover:bg-gray-50'}`}>{p}</button>
                            ))}
                          <button onClick={() => setAdminDeliveryPage(p => Math.min(totalPages, p + 1))} disabled={adminDeliveryPage === totalPages} className="px-2.5 py-1 text-xs font-medium rounded border border-gray-200 disabled:opacity-30 hover:bg-gray-50">Next</button>
                        </div>
                      </div>
                    )}
                    </>
                    );
                  })()}
                </TabsContent>
              ))}
            </Tabs>
          </TabsContent>

          {/* Delete Delivery Confirmation Dialog */}
          <Dialog open={!!deleteDeliveryId} onOpenChange={(open) => { if (!open) setDeleteDeliveryId(null); }}>
            <DialogContent className="max-w-sm" data-testid="delete-delivery-dialog">
              <DialogHeader>
                <DialogTitle className="font-['Inter'] text-red-600 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" /> Delete Delivery
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-2">
                <Alert className="border-red-200 bg-red-50">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-800 text-sm">
                    This will permanently delete this delivery job and all associated offers. This cannot be undone.
                  </AlertDescription>
                </Alert>
                <div className="flex gap-3">
                  <Button onClick={handleDeleteDelivery} className="flex-1 bg-red-600 text-white hover:bg-red-700 rounded-sm" disabled={saving} data-testid="confirm-delete-delivery-btn">
                    {saving ? 'Deleting...' : 'Delete Delivery'}
                  </Button>
                  <Button variant="outline" onClick={() => setDeleteDeliveryId(null)} className="border-gray-300 rounded-sm">Cancel</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Bulk Delete Deliveries Dialog */}
          <Dialog open={bulkDeleteDeliveriesOpen} onOpenChange={setBulkDeleteDeliveriesOpen}>
            <DialogContent className="max-w-sm" data-testid="bulk-delete-deliveries-dialog">
              <DialogHeader>
                <DialogTitle className="font-['Inter'] text-red-600 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" /> Bulk Delete Deliveries
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-2">
                <Alert className="border-red-200 bg-red-50">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-800 text-sm">
                    This will permanently delete <strong>{selectedDeliveryIds.length} delivery(ies)</strong> and all associated offers. This cannot be undone.
                  </AlertDescription>
                </Alert>
                <div className="flex gap-3">
                  <Button onClick={handleBulkDeleteDeliveries} className="flex-1 bg-red-600 text-white hover:bg-red-700 rounded-sm" disabled={saving} data-testid="confirm-bulk-delete-deliveries-btn">
                    {saving ? 'Deleting...' : `Delete ${selectedDeliveryIds.length} Delivery(ies)`}
                  </Button>
                  <Button variant="outline" onClick={() => setBulkDeleteDeliveriesOpen(false)} className="border-gray-300 rounded-sm">Cancel</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* =================== USER MANAGEMENT TAB =================== */}
          <TabsContent value="users">
            {userFilter === 'photo_wall' || userFilter === 'bio_wall' ? (
              <>
                {/* Filter chip row stays mounted above so admins can flip back
                 * to a role-based view; the rest of the standard list UI is
                 * suppressed while we're in gallery mode. */}
                <div className="flex flex-wrap gap-2 mb-4" data-testid="user-management-filters-photowall">
                  {['all', 'admin', 'driver', 'customer'].map(filter => (
                    <Button
                      key={filter}
                      variant="outline"
                      size="sm"
                      onClick={() => { setUserFilter(filter); setUsersPage(1); }}
                      className="border-gray-300"
                      data-testid={`user-filter-${filter}`}
                    >
                      {filter === 'all' ? `All (${users.length})` : `${filter.charAt(0).toUpperCase() + filter.slice(1)}s (${users.filter(u => u.role === filter).length})`}
                    </Button>
                  ))}
                  <Button variant="outline" size="sm" onClick={() => { setUserFilter('business'); setUsersPage(1); }} className="border-blue-300 text-[#0D47A1] hover:bg-blue-50" data-testid="user-filter-business">
                    Businesses ({users.filter(u => u.account_type === 'business').length})
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => { setUserFilter('pending_review'); setUsersPage(1); }} className="border-amber-300 text-amber-800 hover:bg-amber-50" data-testid="user-filter-pending-review">
                    Pending Review ({pendingCount})
                  </Button>
                  <Button
                    variant={userFilter === 'photo_wall' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => { setUserFilter('photo_wall'); setUsersPage(1); }}
                    className={userFilter === 'photo_wall' ? 'bg-[#2E7D32] text-white hover:bg-[#1B5E20]' : 'border-emerald-300 text-[#2E7D32] hover:bg-emerald-50'}
                    data-testid="user-filter-photo-wall-active"
                  >
                    📸 Driver Photos
                  </Button>
                  <Button
                    variant={userFilter === 'bio_wall' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => { setUserFilter('bio_wall'); setUsersPage(1); }}
                    className={userFilter === 'bio_wall' ? 'bg-[#2E7D32] text-white hover:bg-[#1B5E20]' : 'border-emerald-300 text-[#2E7D32] hover:bg-emerald-50'}
                    data-testid="user-filter-bio-wall-active"
                  >
                    💬 Driver Bios
                  </Button>
                </div>
                {userFilter === 'bio_wall' ? (
                  <DriverBioWall users={users} onOpenDriver={openDriverById} />
                ) : (
                  <DriverPhotoWall users={users} onOpenDriver={openDriverById} />
                )}
              </>
            ) : (<>
            <div className="space-y-6">
              {/* Drivers by County Summary */}
              {driversByCounty.length > 0 && (
                <div data-testid="drivers-by-county-section">
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                    <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-[#2E7D32]" /> Drivers by County
                      <span className="text-xs font-normal text-gray-500">— click a county to filter</span>
                    </h3>
                    {countyFilter && (
                      <button
                        type="button"
                        onClick={() => { setCountyFilter(''); setUsersPage(1); }}
                        className="text-xs text-[#2E7D32] hover:underline"
                        data-testid="clear-county-filter-btn"
                      >
                        Clear county filter ({countyFilter})
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {driversByCounty.map(({ county, count, all_ireland }) => {
                      const active = countyFilter === county;
                      const isAll = !!all_ireland;
                      return (
                        <button
                          key={county}
                          type="button"
                          onClick={() => { setCountyFilter(active ? '' : county); setUserFilter('driver'); setUsersPage(1); }}
                          className={[
                            'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border transition-colors',
                            active
                              ? (isAll ? 'bg-blue-600 text-white border-blue-600' : 'bg-[#2E7D32] text-white border-[#2E7D32]')
                              : (isAll
                                  ? 'bg-blue-50 text-blue-900 border-blue-200 hover:border-blue-500 hover:text-blue-700'
                                  : 'bg-gray-100 text-gray-900 border-gray-200 hover:border-[#2E7D32] hover:text-[#2E7D32]')
                          ].join(' ')}
                          data-testid={`county-badge-${county.toLowerCase().replace(/\s+/g, '-')}`}
                          aria-pressed={active}
                        >
                          {isAll ? (
                            <span className="text-base leading-none">🇮🇪</span>
                          ) : (
                            <MapPin className={`w-3 h-3 ${active ? 'text-white' : 'text-[#2E7D32]'}`} />
                          )}
                          <span className="font-medium">{county}</span>
                          <span className={active ? 'text-white/85' : (isAll ? 'text-blue-700' : 'text-gray-500')}>{count} {count === 1 ? 'driver' : 'drivers'}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* User Filter Tabs */}
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex gap-2 flex-wrap">
                  {['all', 'admin', 'driver', 'customer'].map((filter) => (
                    <Button
                      key={filter}
                      variant={userFilter === filter ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => { setUserFilter(filter); setUsersPage(1); }}
                      className={userFilter === filter ? 'bg-[#2E7D32] text-white hover:bg-[#1B5E20]' : 'border-gray-300'}
                      data-testid={`user-filter-${filter}`}
                    >
                      {filter === 'all' ? `All (${users.length})` : `${filter.charAt(0).toUpperCase() + filter.slice(1)}s (${users.filter(u => u.role === filter).length})`}
                    </Button>
                  ))}
                  <Button
                    variant={userFilter === 'business' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => { setUserFilter('business'); setUsersPage(1); }}
                    className={userFilter === 'business' ? 'bg-[#0D47A1] text-white hover:bg-[#0B3E8C]' : 'border-blue-300 text-[#0D47A1] hover:bg-blue-50'}
                    data-testid="user-filter-business"
                  >
                    Businesses ({users.filter(u => u.account_type === 'business').length})
                  </Button>
                  <Button
                    variant={userFilter === 'pending_review' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => { setUserFilter('pending_review'); setUsersPage(1); }}
                    className={userFilter === 'pending_review' ? 'bg-amber-600 text-white hover:bg-amber-700' : 'border-amber-300 text-amber-800 hover:bg-amber-50'}
                    data-testid="user-filter-pending-review"
                  >
                    Pending Review ({pendingCount})
                  </Button>
                  <Button
                    variant={userFilter === 'premium' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => { setUserFilter('premium'); setUsersPage(1); }}
                    className={userFilter === 'premium' ? 'bg-[#1B5E20] text-white hover:bg-[#164a1a] gap-1' : 'border-emerald-300 text-[#1B5E20] hover:bg-emerald-50 gap-1'}
                    data-testid="user-filter-premium"
                  >
                    <Star className="w-3.5 h-3.5 fill-current" />
                    Premium ({users.filter(u => u.role === 'driver' && u.is_premium).length})
                  </Button>
                  <Button
                    variant={userFilter === 'photo_wall' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => { setUserFilter('photo_wall'); setUsersPage(1); }}
                    className={userFilter === 'photo_wall' ? 'bg-[#2E7D32] text-white hover:bg-[#1B5E20]' : 'border-emerald-300 text-[#2E7D32] hover:bg-emerald-50'}
                    data-testid="user-filter-photo-wall"
                  >
                    📸 Driver Photos
                  </Button>
                  <Button
                    variant={userFilter === 'bio_wall' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => { setUserFilter('bio_wall'); setUsersPage(1); }}
                    className={userFilter === 'bio_wall' ? 'bg-[#2E7D32] text-white hover:bg-[#1B5E20]' : 'border-emerald-300 text-[#2E7D32] hover:bg-emerald-50'}
                    data-testid="user-filter-bio-wall"
                  >
                    💬 Driver Bios
                  </Button>
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search name, email, phone..."
                      value={userSearch}
                      onChange={(e) => { setUserSearch(e.target.value); setUsersPage(1); }}
                      className="pl-9 h-9 w-60 rounded-sm border-gray-300 text-sm"
                      data-testid="user-search-input"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={fetchUsers} className="border-gray-300 gap-1" data-testid="refresh-users-btn">
                    Refresh
                  </Button>
                  {selectedUserIds.length > 0 && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setBulkDeleteDialogOpen(true)}
                      className="gap-1"
                      data-testid="bulk-delete-btn"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Delete Selected ({selectedUserIds.length})
                    </Button>
                  )}
                </div>
              </div>

              {/* Users Table */}
              {usersLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Users className="w-8 h-8 text-gray-400 animate-pulse" />
                </div>
              ) : filteredUsers.length === 0 ? (
                <Card className="border border-gray-200 shadow-none">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <Users className="w-16 h-16 text-gray-300 mb-4" strokeWidth={1} />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No users found</h3>
                  </CardContent>
                </Card>
              ) : (() => {
                const USERS_PER_PAGE = 20;
                const totalUserPages = Math.ceil(filteredUsers.length / USERS_PER_PAGE);
                const pagedUsers = filteredUsers.slice((usersPage - 1) * USERS_PER_PAGE, usersPage * USERS_PER_PAGE);
                return (
                <>
                <Card className="border border-gray-200 shadow-none overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50 hover:bg-gray-50">
                        <TableHead className="w-10">
                          <input
                            type="checkbox"
                            checked={pagedUsers.filter(u => u.role !== 'admin').length > 0 && pagedUsers.filter(u => u.role !== 'admin').every(u => selectedUserIds.includes(u.id))}
                            onChange={toggleSelectAll}
                            className="w-4 h-4 rounded border-gray-300 accent-[#2E7D32] cursor-pointer"
                            data-testid="select-all-users"
                          />
                        </TableHead>
                        <TableHead className="font-semibold text-gray-900">Name</TableHead>
                        <TableHead className="font-semibold text-gray-900">Email</TableHead>
                        <TableHead className="font-semibold text-gray-900">Phone</TableHead>
                        <TableHead className="font-semibold text-gray-900">Role</TableHead>
                        <TableHead className="font-semibold text-gray-900">Location</TableHead>
                        <TableHead className="font-semibold text-gray-900">Vehicle</TableHead>
                        <TableHead className="font-semibold text-gray-900">Status</TableHead>
                        <TableHead className="font-semibold text-gray-900 text-center">Jobs</TableHead>
                        <TableHead className="font-semibold text-gray-900 text-center">Rating</TableHead>
                        <TableHead className="font-semibold text-gray-900 text-center">Reviews</TableHead>
                        <TableHead className="font-semibold text-gray-900 text-right min-w-[200px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pagedUsers.map((u) => (
                        <TableRow key={u.id} className={`hover:bg-gray-50 ${u.banned ? 'bg-red-50/50' : ''} ${selectedUserIds.includes(u.id) ? 'bg-blue-50/50' : ''}`} data-testid={`user-row-${u.id}`}>
                          <TableCell>
                            {u.role !== 'admin' ? (
                              <input
                                type="checkbox"
                                checked={selectedUserIds.includes(u.id)}
                                onChange={() => toggleSelectUser(u.id)}
                                className="w-4 h-4 rounded border-gray-300 accent-[#2E7D32] cursor-pointer"
                                data-testid={`select-user-${u.id}`}
                              />
                            ) : <span className="w-4 h-4 block" />}
                          </TableCell>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span>{u.name}</span>
                              {u.role === 'driver' && u.is_premium && <PremiumBadge size="sm" dataTestId={`user-premium-badge-${u.id}`} />}
                            </div>
                            {u.account_type === 'business' && u.company_name && (
                              <div className="flex items-center gap-1 mt-0.5">
                                <span className="text-[10px] font-bold uppercase tracking-wider text-blue-700 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded-full">Business</span>
                                <span className="text-xs text-gray-500">{u.company_name}</span>
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-sm">{u.email}</TableCell>
                          <TableCell className="text-sm text-gray-600">{u.phone}</TableCell>
                          <TableCell>{roleBadge(u.role)}</TableCell>
                          <TableCell className="text-sm text-gray-600" data-testid={`user-location-${u.id}`}>
                            {u.serves_all_ireland ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 border border-blue-200 text-blue-800 text-[11px] font-medium rounded-full">
                                🇮🇪 Anywhere in Ireland
                              </span>
                            ) : Array.isArray(u.counties) && u.counties.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {u.counties.map(c => (
                                  <span key={c} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-emerald-50 border border-emerald-200 text-emerald-800 text-[10px] font-medium rounded-full">
                                    <MapPin className="w-2.5 h-2.5" /> {c}
                                  </span>
                                ))}
                                {u.town && <span className="text-[10px] text-gray-400 ml-1">{u.town}</span>}
                              </div>
                            ) : u.location ? (
                              <span className="inline-flex items-center gap-1 text-gray-500">
                                <MapPin className="w-3 h-3 text-gray-400" /> {u.location}
                              </span>
                            ) : <span className="text-xs text-gray-300">—</span>}
                          </TableCell>
                          <TableCell data-testid={`user-vehicle-${u.id}`} className="min-w-[150px]">
                            {u.role === 'driver' ? (
                              (u.vehicles || []).length > 0 ? (
                                <div
                                  className="flex items-center gap-1 flex-nowrap"
                                  title={u.vehicles.map((v) => [
                                    v.type, v.make_model, v.load_capacity && `Capacity: ${v.load_capacity}`,
                                    v.tail_lift && 'Tail lift',
                                  ].filter(Boolean).join(' · ')).join('  |  ')}
                                >
                                  {u.vehicles.map((v, vi) => (
                                    <span key={`${v.type}-${vi}`} className="px-1.5 py-0.5 rounded bg-gray-100 border border-gray-200 text-[10px] font-semibold text-gray-700 whitespace-nowrap max-w-[96px] truncate">{v.type}</span>
                                  ))}
                                  {u.vehicles.some((v) => v.tail_lift) && (
                                    <span className="px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-[10px] font-semibold text-emerald-800 whitespace-nowrap">Lift</span>
                                  )}
                                </div>
                              ) : <span className="text-xs text-gray-400 italic whitespace-nowrap">Not added</span>
                            ) : <span className="text-xs text-gray-300">—</span>}
                          </TableCell>

                          <TableCell>
                            {u.banned ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium" data-testid={`user-banned-${u.id}`}>
                                <Ban className="w-3 h-3" /> Banned
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium" data-testid={`user-active-${u.id}`}>
                                <CheckCircle className="w-3 h-3" /> Active
                              </span>
                            )}
                            {u.role === 'driver' && u.compliance_status && u.compliance_status !== 'active' && (
                              <span
                                className={`mt-1 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-full ${u.compliance_status === 'rejected' ? 'bg-red-50 border border-red-200 text-red-700' : 'bg-amber-50 border border-amber-200 text-amber-800'}`}
                                data-testid={`compliance-status-${u.id}`}
                                title={u.compliance_status === 'rejected' ? 'Compliance rejected' : (u.compliance_status === 'pending_review' ? 'Awaiting admin review' : 'Restricted — driver must complete compliance')}
                              >
                                {u.compliance_status === 'rejected' ? 'Rejected' : (u.compliance_status === 'pending_review' ? 'Pending Review' : 'Restricted')}
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-center" data-testid={`user-jobs-${u.id}`}>
                            {u.role === 'driver' ? (
                              <span className="text-sm font-medium">{u.jobs_completed || 0}</span>
                            ) : <span className="text-xs text-gray-300">—</span>}
                          </TableCell>
                          <TableCell className="text-center" data-testid={`user-rating-${u.id}`}>
                            {u.role === 'driver' ? (
                              u.avg_rating > 0 ? (
                                <span className="inline-flex items-center gap-1 text-sm font-medium">
                                  <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" /> {u.avg_rating}
                                </span>
                              ) : <span className="text-xs text-gray-400 italic">New</span>
                            ) : <span className="text-xs text-gray-300">—</span>}
                          </TableCell>
                          <TableCell className="text-center" data-testid={`user-reviews-${u.id}`}>
                            {u.role === 'driver' ? (
                              <span className="text-sm">{u.review_count || 0}</span>
                            ) : <span className="text-xs text-gray-300">—</span>}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1 flex-nowrap">
                              {u.role === 'driver' && (
                                <Button variant="ghost" size="sm" onClick={() => openDriverDetail(u)} className="gap-1 hover:bg-blue-50 text-blue-600 px-2" data-testid={`view-driver-btn-${u.id}`}>
                                  <Eye className="w-3.5 h-3.5" /> View
                                </Button>
                              )}
                              {u.role === 'driver' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openLicenceViewer(u)}
                                  className="gap-1 hover:bg-gray-100 text-gray-700 px-2"
                                  data-testid={`view-licence-btn-${u.id}`}
                                  title="View driving licence on file"
                                >
                                  <KeyRound className="w-3.5 h-3.5" /> Licence
                                </Button>
                              )}
                              {u.role === 'driver' && (u.compliance_status === 'pending_review' || u.compliance_status === 'restricted' || u.compliance_status === 'rejected') && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openComplianceReview(u)}
                                  className="gap-1 text-amber-700 hover:bg-amber-50 px-2"
                                  data-testid={`review-compliance-btn-${u.id}`}
                                >
                                  <ShieldAlert className="w-3.5 h-3.5" /> Review
                                </Button>
                              )}
                              <Button variant="ghost" size="sm" onClick={() => openEditDialog(u)} className="gap-1 hover:bg-gray-100 px-2" data-testid={`edit-user-btn-${u.id}`}>
                                <Pencil className="w-3.5 h-3.5" /> Edit
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setNotesUser(u)}
                                className={`gap-1 px-2 ${u.notes_count ? 'text-amber-700 hover:bg-amber-50' : 'text-gray-600 hover:bg-gray-100'}`}
                                title="Internal admin notes"
                                data-testid={`notes-user-btn-${u.id}`}
                              >
                                <StickyNote className="w-3.5 h-3.5" />
                                Notes{u.notes_count ? ` (${u.notes_count})` : ''}
                              </Button>
                              {u.role === 'driver' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleTogglePremium(u)}
                                  disabled={!!premiumPending[u.id]}
                                  className={`gap-1 px-2 ${u.is_premium
                                    ? 'text-red-600 hover:bg-red-50'
                                    : 'text-[#1B5E20] hover:bg-emerald-50'} disabled:opacity-50`}
                                  data-testid={`toggle-premium-btn-${u.id}`}
                                  title={u.is_premium ? 'Remove premium status' : 'Promote to premium driver'}
                                >
                                  <Star className={`w-3.5 h-3.5 ${u.is_premium ? '' : 'fill-current'}`} />
                                  {u.is_premium ? 'Remove Premium' : 'Make Premium'}
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleToggleBan(u)}
                                className={`gap-1 px-2 ${u.banned ? 'text-emerald-600 hover:bg-emerald-50' : 'text-amber-600 hover:bg-amber-50'}`}
                                data-testid={`ban-user-btn-${u.id}`}
                              >
                                {u.banned ? <><CheckCircle className="w-3.5 h-3.5" /> Unban</> : <><Ban className="w-3.5 h-3.5" /> Ban</>}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => { setSelectedUser(u); setDeleteDialogOpen(true); }}
                                className="gap-1 text-red-600 hover:bg-red-50 px-2"
                                data-testid={`delete-user-btn-${u.id}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" /> Delete
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
                {/* Users Pagination */}
                {totalUserPages > 1 && (
                  <div className="flex items-center justify-between mt-3 px-1">
                    <p className="text-xs text-gray-500">{filteredUsers.length} users • Page {usersPage} of {totalUserPages}</p>
                    <div className="flex items-center gap-1">
                      <button onClick={() => setUsersPage(p => Math.max(1, p - 1))} disabled={usersPage === 1} className="px-2.5 py-1 text-xs font-medium rounded border border-gray-200 disabled:opacity-30 hover:bg-gray-50">Prev</button>
                      {Array.from({ length: totalUserPages }, (_, i) => i + 1)
                        .filter(p => p === 1 || p === totalUserPages || Math.abs(p - usersPage) <= 2)
                        .reduce((acc, p, i, arr) => { if (i > 0 && p - arr[i - 1] > 1) acc.push('...'); acc.push(p); return acc; }, [])
                        .map((p, i) => p === '...' ? (
                          <span key={`e-${i}`} className="px-1 text-xs text-gray-400">...</span>
                        ) : (
                          <button key={p} onClick={() => setUsersPage(p)} className={`w-7 h-7 text-xs font-medium rounded ${p === usersPage ? 'bg-[#2E7D32] text-white' : 'border border-gray-200 hover:bg-gray-50'}`}>{p}</button>
                        ))}
                      <button onClick={() => setUsersPage(p => Math.min(totalUserPages, p + 1))} disabled={usersPage === totalUserPages} className="px-2.5 py-1 text-xs font-medium rounded border border-gray-200 disabled:opacity-30 hover:bg-gray-50">Next</button>
                    </div>
                  </div>
                )}
                </>
                );
              })()}
            </div>

            {/* Edit User Dialog */}
            <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
              <DialogContent className="max-w-md" data-testid="edit-user-dialog">
                <DialogHeader>
                  <DialogTitle className="font-['Inter']">Edit User</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-2">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Name</Label>
                    <Input
                      value={editForm.name}
                      onChange={(e) => setEditForm(p => ({ ...p, name: e.target.value }))}
                      className="rounded-sm"
                      data-testid="edit-user-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Email</Label>
                    <Input
                      type="email"
                      value={editForm.email}
                      onChange={(e) => setEditForm(p => ({ ...p, email: e.target.value }))}
                      className="rounded-sm"
                      data-testid="edit-user-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Phone</Label>
                    <Input
                      value={editForm.phone}
                      onChange={(e) => setEditForm(p => ({ ...p, phone: e.target.value }))}
                      className="rounded-sm"
                      data-testid="edit-user-phone"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Role</Label>
                    <select
                      value={editForm.role}
                      onChange={(e) => setEditForm(p => ({ ...p, role: e.target.value }))}
                      className="w-full border border-gray-200 rounded-sm px-3 py-2 text-sm"
                      data-testid="edit-user-role"
                    >
                      <option value="customer">Customer</option>
                      <option value="driver">Driver</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">New Password <span className="text-gray-400 font-normal">(leave blank to keep current)</span></Label>
                    <Input
                      type="password"
                      placeholder="Enter new password"
                      value={editForm.password}
                      onChange={(e) => setEditForm(p => ({ ...p, password: e.target.value }))}
                      className="rounded-sm"
                      data-testid="edit-user-password"
                    />
                  </div>
                  <div className="flex gap-3 pt-2">
                    <Button
                      onClick={handleEditSave}
                      className="flex-1 bg-[#2E7D32] text-white hover:bg-[#1B5E20] rounded-sm"
                      disabled={saving}
                      data-testid="save-user-btn"
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                    <Button variant="outline" onClick={() => setEditDialogOpen(false)} className="border-gray-300 rounded-sm">
                      Cancel
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
              <DialogContent className="max-w-sm" data-testid="delete-user-dialog">
                <DialogHeader>
                  <DialogTitle className="font-['Inter'] text-red-600 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" /> Delete User
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-2">
                  <Alert className="border-red-200 bg-red-50">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                    <AlertDescription className="text-red-800 text-sm">
                      This will permanently delete <strong>{selectedUser?.name}</strong> ({selectedUser?.email}) and all their offers. This action cannot be undone.
                    </AlertDescription>
                  </Alert>
                  <div className="flex gap-3">
                    <Button
                      onClick={handleDelete}
                      className="flex-1 bg-red-600 text-white hover:bg-red-700 rounded-sm"
                      disabled={saving}
                      data-testid="confirm-delete-btn"
                    >
                      {saving ? 'Deleting...' : 'Delete User'}
                    </Button>
                    <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="border-gray-300 rounded-sm">
                      Cancel
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Bulk Delete Confirmation Dialog */}
            <Dialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
              <DialogContent className="max-w-sm" data-testid="bulk-delete-dialog">
                <DialogHeader>
                  <DialogTitle className="font-['Inter'] text-red-600 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" /> Bulk Delete Users
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-2">
                  <Alert className="border-red-200 bg-red-50">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                    <AlertDescription className="text-red-800 text-sm">
                      This will permanently delete <strong>{selectedUserIds.length} user(s)</strong> and all their offers. This action cannot be undone.
                    </AlertDescription>
                  </Alert>
                  <div className="flex gap-3">
                    <Button
                      onClick={handleBulkDelete}
                      className="flex-1 bg-red-600 text-white hover:bg-red-700 rounded-sm"
                      disabled={saving}
                      data-testid="confirm-bulk-delete-btn"
                    >
                      {saving ? 'Deleting...' : `Delete ${selectedUserIds.length} User(s)`}
                    </Button>
                    <Button variant="outline" onClick={() => setBulkDeleteDialogOpen(false)} className="border-gray-300 rounded-sm">
                      Cancel
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            </>)}{/* end inner-view conditional (list vs photo/bio wall) */}

            {/* Internal admin notes — mounted for both the list and the
                photo/bio gallery views. */}
            <AdminNotesDialog
              open={!!notesUser}
              user={notesUser}
              onOpenChange={(open) => { if (!open) setNotesUser(null); }}
              onCountChange={(userId, count) =>
                setUsers((list) => list.map((u) => (u.id === userId ? { ...u, notes_count: count } : u)))}
            />

            {/* Driver Detail Dialog */}
            <Dialog open={driverDetailOpen} onOpenChange={setDriverDetailOpen}>
              <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="driver-detail-dialog">
                <DialogHeader>
                  <DialogTitle className="font-['Inter'] flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-blue-600" /> {driverDetailUser?.name} — Performance
                  </DialogTitle>
                </DialogHeader>

                {driverDetailLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Package className="w-6 h-6 text-gray-400 animate-pulse" />
                  </div>
                ) : driverDetailData ? (
                  <div className="space-y-5 pt-2">
                    {/* Driver Profile Card */}
                    <div className="flex items-center gap-4 p-4 bg-gray-50 border" data-testid="driver-detail-profile">
                      {driverDetailData.driver?.profile_image ? (
                        <img src={`${process.env.REACT_APP_BACKEND_URL}${driverDetailData.driver.profile_image}`} alt={driverDetailUser?.name} className="w-16 h-16 rounded-full object-cover border-2 border-[#2E7D32]" onError={(e) => { e.target.onerror=null; e.target.style.display='none'; e.target.parentElement.querySelector('.admin-photo-fallback').style.display='flex'; }} />
                      ) : null}
                      <div className={`admin-photo-fallback w-16 h-16 rounded-full bg-gray-200 items-center justify-center border-2 border-gray-300`} style={{ display: driverDetailData.driver?.profile_image ? 'none' : 'flex' }}>
                        <User className="w-7 h-7 text-gray-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900">{driverDetailData.driver?.name || driverDetailUser?.name}</p>
                        <p className="text-sm text-gray-500">{driverDetailData.driver?.email}</p>
                        <p className="text-sm text-gray-500">{driverDetailData.driver?.phone}</p>
                        {driverDetailData.driver?.bio ? (
                          <p className="text-sm text-gray-600 italic mt-1" data-testid="driver-detail-bio">"{driverDetailData.driver.bio}"</p>
                        ) : (
                          <p className="text-xs text-gray-400 mt-1 italic" data-testid="driver-detail-no-bio">No bio added</p>
                        )}
                      </div>
                    </div>

                    {/* Vehicle details from the driver's bidding profile */}
                    <div className="p-4 border bg-white" data-testid="driver-detail-vehicle">
                      <p className="text-xs font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1.5 mb-2">
                        <Truck className="w-3.5 h-3.5 text-gray-400" /> Vehicles
                      </p>
                      {(driverDetailData.driver?.vehicle?.vehicles || []).length > 0 ? (
                        <div className="space-y-3">
                          {driverDetailData.driver.vehicle.vehicles.map((v, vi) => (
                            <div key={`${v.type}-${vi}`} className={vi > 0 ? 'pt-3 border-t border-dashed border-gray-200' : ''}>
                              <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">
                                {vi === 0 ? 'Main vehicle' : 'Second vehicle'}
                              </p>
                              <div className="flex flex-wrap gap-1.5">
                                <span className="px-2 py-0.5 rounded bg-gray-100 border border-gray-200 text-[11px] font-semibold text-gray-700">{v.type}</span>
                                {v.tail_lift && (
                                  <span className="px-2 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-[11px] font-semibold text-emerald-800">Tail lift</span>
                                )}
                              </div>
                              {(v.make_model || v.load_capacity) && (
                                <p className="text-sm text-gray-700 break-words mt-1">
                                  {v.make_model}
                                  {v.make_model && v.load_capacity && ' · '}
                                  {v.load_capacity && `Capacity: ${v.load_capacity}`}
                                </p>
                              )}
                              {v.notes && <p className="text-xs text-gray-500 break-words mt-0.5">{v.notes}</p>}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-400 italic" data-testid="driver-detail-no-vehicle">No vehicle details added</p>
                      )}
                    </div>

                    {/* Summary */}
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-gray-50 p-3 text-center border">
                        <p className="text-2xl font-bold text-gray-900" data-testid="driver-detail-jobs">{driverDetailData.deliveries.length}</p>
                        <p className="text-xs text-gray-500">Jobs Completed</p>
                      </div>
                      <div className="bg-gray-50 p-3 text-center border">
                        <p className="text-2xl font-bold text-gray-900 flex items-center justify-center gap-1" data-testid="driver-detail-rating">
                          {driverDetailUser?.avg_rating > 0 ? (
                            <><Star className="w-5 h-5 fill-yellow-400 text-yellow-400" /> {driverDetailUser.avg_rating}</>
                          ) : '—'}
                        </p>
                        <p className="text-xs text-gray-500">Avg Rating</p>
                      </div>
                      <div className="bg-gray-50 p-3 text-center border">
                        <p className="text-2xl font-bold text-gray-900" data-testid="driver-detail-review-count">{driverDetailData.reviews.length}</p>
                        <p className="text-xs text-gray-500">Reviews</p>
                      </div>
                    </div>

                    {/* Completed Jobs */}
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-1"><Package className="w-4 h-4" /> Completed Jobs</h3>
                      {driverDetailData.deliveries.length === 0 ? (
                        <p className="text-sm text-gray-400 italic">No completed jobs yet</p>
                      ) : (
                        <div className="space-y-2 max-h-[200px] overflow-y-auto">
                          {driverDetailData.deliveries.map((d) => (
                            <div key={d.id} className="p-2 bg-gray-50 border text-sm flex justify-between items-center" data-testid={`driver-job-${d.id}`}>
                              <div>
                                <p className="font-medium">{d.pickup_county} → {d.dropoff_county}</p>
                                <p className="text-xs text-gray-500">{d.customer_name} • {d.item_description?.substring(0, 40)}</p>
                              </div>
                              <span className="font-bold text-[#2E7D32]">€{d.assigned_price?.toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Reviews */}
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-1"><Star className="w-4 h-4" /> Reviews</h3>
                      {driverDetailData.reviews.length === 0 ? (
                        <p className="text-sm text-gray-400 italic">No reviews yet</p>
                      ) : (
                        <div className="space-y-2 max-h-[200px] overflow-y-auto">
                          {driverDetailData.reviews.map((r, i) => (
                            <div key={i} className="p-2 bg-gray-50 border text-sm" data-testid={`driver-review-${i}`}>
                              <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-1">
                                  {[1,2,3,4,5].map(s => (
                                    <Star key={s} className={`w-3.5 h-3.5 ${s <= r.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                                  ))}
                                </div>
                                <span className="text-xs text-gray-400">{new Date(r.created_at).toLocaleDateString('en-IE')}</span>
                              </div>
                              <p className="text-xs text-gray-500">{r.customer_name}</p>
                              {r.comment && <p className="text-sm text-gray-700 mt-1 italic">"{r.comment}"</p>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ) : null}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* =================== SUPPORT TAB =================== */}
          <TabsContent value="support">
            <AdminSupportInbox />
          </TabsContent>

          {/* =================== ACTIVITY LOG TAB =================== */}
          <TabsContent value="activity">
            <AdminAuditLog />
          </TabsContent>

          {/* =================== EARNINGS & REVENUE TAB =================== */}
          <TabsContent value="earnings">
            <EarningsPanel />
          </TabsContent>

          {/* =================== HOMEPAGE CMS TAB =================== */}
          <TabsContent value="cms">
            <HomepageCMSPanel />
          </TabsContent>

          {/* =================== CONVERSATIONS TAB =================== */}
          <TabsContent value="conversations">
            <AdminConversations />
          </TabsContent>

          {/* =================== INSURANCE TAB =================== */}
          <TabsContent value="insurance">
            <InsuranceReviewPanel />
          </TabsContent>

          {/* =================== DIRECTORY APPROVALS TAB =================== */}
          <TabsContent value="directory">
            <AdminDirectoryApprovals />
          </TabsContent>

        </main>
      </Tabs>

      {/* Quick Licence Viewer */}
      <Dialog open={!!licenceViewUser} onOpenChange={(o) => { if (!o) { setLicenceViewUser(null); setLicenceViewSrc(null); setLicenceViewBackSrc(null); } }}>
        <DialogContent className="max-w-2xl rounded-sm" data-testid="licence-viewer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-[#1B5E20]" />
              Driving licence
            </DialogTitle>
            <DialogDescription>
              {licenceViewUser?.name} · {licenceViewUser?.email}
            </DialogDescription>
          </DialogHeader>
          {licenceViewLoading ? (
            <div className="flex items-center justify-center py-10 text-gray-400">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : (licenceViewSrc || licenceViewBackSrc) ? (
            <div className="space-y-4">
              {licenceViewSrc && (
                <div>
                  <p className="text-xs font-semibold text-gray-700 mb-1.5">Front</p>
                  <a href={licenceViewSrc} target="_blank" rel="noreferrer" className="block" data-testid="licence-viewer-link">
                    <img src={licenceViewSrc} alt="Driving licence front" className="w-full max-h-[55vh] object-contain rounded-md border border-gray-200 bg-gray-50" />
                  </a>
                </div>
              )}
              {licenceViewBackSrc ? (
                <div>
                  <p className="text-xs font-semibold text-gray-700 mb-1.5">Back <span className="text-gray-400 font-normal">(vehicle categories)</span></p>
                  <a href={licenceViewBackSrc} target="_blank" rel="noreferrer" className="block" data-testid="licence-viewer-back-link">
                    <img src={licenceViewBackSrc} alt="Driving licence back" className="w-full max-h-[55vh] object-contain rounded-md border border-gray-200 bg-gray-50" />
                  </a>
                </div>
              ) : (
                <p className="text-xs text-gray-500 italic" data-testid="licence-viewer-back-missing">
                  No back-of-licence photo on file yet. Drivers can add this from the compliance gate.
                </p>
              )}
              <p className="text-center text-xs text-gray-500">Click any image to open full-size in a new tab</p>
            </div>
          ) : (
            <div className="py-10 text-center text-sm text-gray-500" data-testid="licence-viewer-empty">
              No driving licence on file for this driver yet.
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Compliance Review Dialog */}
      <Dialog open={!!complianceUser} onOpenChange={(o) => { if (!o) setComplianceUser(null); }}>
        <DialogContent className="max-w-xl rounded-sm" data-testid="compliance-review-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-amber-600" />
              Review driver compliance
            </DialogTitle>
            <DialogDescription>
              {complianceUser?.name} · {complianceUser?.email}
            </DialogDescription>
          </DialogHeader>
          {complianceUser && (
            <div className="space-y-4">
              {/* Quick facts */}
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="p-2 bg-gray-50 border border-gray-200 rounded">
                  <div className="text-gray-500">Vehicle</div>
                  <div className="font-medium capitalize">{complianceUser.vehicle_type || '—'}</div>
                </div>
                <div className="p-2 bg-gray-50 border border-gray-200 rounded">
                  <div className="text-gray-500">VAT registered</div>
                  <div className="font-medium">
                    {complianceUser.vat_registered ? (complianceUser.vat_number || 'Yes') : 'No'}
                  </div>
                </div>
                <div className="p-2 bg-gray-50 border border-gray-200 rounded">
                  <div className="text-gray-500">Counties</div>
                  <div className="font-medium">
                    {complianceUser.serves_all_ireland ? 'Anywhere in Ireland' : (complianceUser.counties?.join(', ') || '—')}
                  </div>
                </div>
                <div className="p-2 bg-gray-50 border border-gray-200 rounded">
                  <div className="text-gray-500">Submitted</div>
                  <div className="font-medium">{complianceUser.compliance_submitted_at ? new Date(complianceUser.compliance_submitted_at).toLocaleString() : '—'}</div>
                </div>
              </div>

              {/* Licence preview */}
              <div>
                <p className="text-xs font-medium text-gray-700 mb-1.5">Driving licence — Front</p>
                {complianceLoading ? (
                  <div className="h-48 flex items-center justify-center bg-gray-50 border border-gray-200 rounded">
                    <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                  </div>
                ) : complianceLicence ? (
                  <a href={complianceLicence} target="_blank" rel="noreferrer" className="block">
                    <img
                      src={complianceLicence}
                      alt="Driving licence front"
                      className="w-full max-h-72 object-contain rounded border border-gray-200 bg-gray-50 cursor-zoom-in"
                      data-testid="compliance-licence-img"
                    />
                  </a>
                ) : (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900">
                    No licence on file. Driver must upload one before approval.
                  </div>
                )}
              </div>

              {/* Licence back preview */}
              {!complianceLoading && (
                <div>
                  <p className="text-xs font-medium text-gray-700 mb-1.5">Driving licence — Back <span className="text-gray-400 font-normal">(vehicle categories)</span></p>
                  {complianceLicenceBack ? (
                    <a href={complianceLicenceBack} target="_blank" rel="noreferrer" className="block">
                      <img
                        src={complianceLicenceBack}
                        alt="Driving licence back"
                        className="w-full max-h-72 object-contain rounded border border-gray-200 bg-gray-50 cursor-zoom-in"
                        data-testid="compliance-licence-back-img"
                      />
                    </a>
                  ) : (
                    <div className="p-3 bg-gray-50 border border-gray-200 rounded text-xs text-gray-500 italic" data-testid="compliance-licence-back-missing">
                      No back-of-licence photo on file yet.
                    </div>
                  )}
                </div>
              )}

              <div>
                <Label className="text-xs text-gray-500">Note (required when rejecting)</Label>
                <Input
                  placeholder="e.g. Licence expired — please upload a current one"
                  value={complianceNote}
                  onChange={(e) => setComplianceNote(e.target.value)}
                  className="rounded-sm text-sm mt-1"
                  data-testid="compliance-note-input"
                />
                <p className="text-[11px] text-gray-500 mt-1.5" data-testid="compliance-email-hint">
                  This note is emailed to <strong>{complianceUser.email}</strong> with the decision — write it for the driver to read.
                </p>
              </div>

              <div className="flex gap-2 pt-1">
                <Button
                  onClick={() => handleComplianceDecision('reject')}
                  variant="outline"
                  className="flex-1 border-red-300 text-red-700 hover:bg-red-50"
                  data-testid="compliance-reject-btn"
                >
                  Reject
                </Button>
                <Button
                  onClick={() => handleComplianceDecision('approve')}
                  className="flex-1 bg-[#2E7D32] text-white hover:bg-[#1B5E20]"
                  disabled={!complianceLicence}
                  data-testid="compliance-approve-btn"
                >
                  Approve & activate
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <CancelWithCompensationDialog
        delivery={compDelivery}
        open={!!compDelivery}
        onOpenChange={(open) => { if (!open) setCompDelivery(null); }}
        onDone={() => { fetchData(); loadOwedComps(); }}
      />
    </div>
  );
}
